
import {
  computed,
  ComputedRef,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  Ref,
  watch
} from "vue";

import { useStore } from "@/store";
import {
  ElementPartTypeEnum,
  HistoryType,
  TemplateParentTypeEnum,
  TemplateStructureEnum,
  TemplateWidth,
  WidgetTypeEnum
} from "@/types/enums";
import {
  TEMPLATE_PAGE_IDENTIFIER,
  MEDIA_URL_PART_THUMB_GENERATION,
  HIDE_MOBILE_ATTRIBUTE,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
  TEMPLATE_SYNC_ELEMENT_CLASS,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_SYNC_ELEMENT_IDs,
  TEMPLATE_PART_ELEMENT_SUBTYPE,
  TEMPLATE_PART_ELEMENT_IDENTIFIER,
} from "@/components/template-builder/utils/constants";
import ConfigurationPanel from "@/components/template-builder/panels/ConfigurationPanel.vue";
import InformationPanel from "@/components/template-builder/panels/InformationPanel.vue";
import TranslationPanel from "@/components/template-builder/panels/TranslationPanel.vue";
import HistoryPanel from "@/components/template-builder/panels/HistoryPanel.vue";
import HistoryRestoreLoader from "@/components/template-builder/HistoryRestoreLoader.vue";
import CssPanel from "@/components/template-builder/panels/CssPanel.vue";
import CssPanelMediaQueries from "@/components/template-builder/panels/CssPanelMediaQueries.vue";
import LoadElementsPanel from "@/components/template-builder/panels/LoadElementsPanel.vue";
import TemplateLiveView from "@/components/template-builder/TemplateLiveView.vue";
import InputTextModal from "@/components/modals/InputTextModal.vue";
import SaveElementModal from "@/components/modals/SaveElementModal.vue";
import WidgetsPanel from "@/components/template-builder/panels/WidgetsPanel.vue";
import FunnelStep from "@/components/template-builder/FunnelStep.vue";
import ColumnWidgetModal from "@/components/modals/ColumnWidgetModal.vue";
import LoadElementsModal from "@/components/modals/LoadElementsModal.vue";
import SearchElementsModal from "@/components/modals/SearchElementsModal.vue";
import SyncElementUsedTemplates from "@/components/template-builder/SyncElementUsedTemplates.vue";
import ActiveCampaignsList from "@/components/template-builder/ActiveCampaignsList.vue";
import useToggleBodyScrollBars from "@/composables/useToggleBodyScrollBars";
import { openHelp } from "@/helpers/Gleap";
import {
  activate,
  Template,
  getTemplatePartsById,
  clearVersionConflicts,
  deactivate,
  hideConfigurationPanel,
  hideTemplateEditor,
  refreshSectionsConfig,
  refreshTranslations,
  reloadTemplateFromDatabase,
  rename,
  savePartInDatabase,
  saveTemplatesInLocalStorage,
  setPreventDateModificationUpdate,
  setRefreshTemplatesList,
  showConfigurationPanel,
  TemplateEditorState as state,
  toggleAdvancedMode,
  togglePanel,
  updateSectionsInState,
  sendTestNotification,
  getTestPushNotificationData,
  beforeSaveTemplate,
  saveTemplate,
  setTemplateConfigurationKeyInState,
  informationPanelSettingsValidate,
  updateLastRollbackGroupId,
  setTestEmailSent,
  resetNeedToTranslate,
  resetTemplateToDesktop,
  setTemplateWidth,
  setEditorViewport,
  isMobileViewport,
  refreshSmartProductsListsOriginalStructure,
  refreshAttributeInDOM,
  restoreSmartListsOriginalCode,
  sortTranslationInState,
  removeUnusedTranslations,
  updateFormWidgets,
  getCurrentTemplateVersion,
  hideLeftToolbar,
  restoreState,
  unlinkSyncElement,
  linkSection,
  addNewSyncElementToSection,
  renamePartInDatabase,
  updateUsedSyncElements,
  getAllSyncElements,
  removeCustomWidgetCss,
  getTemplatePartsBySyncElementIds,
  updateUsedSyncElementsName,
  updateFormsInState,
  insertSavedElement,
  startHistoryRestore,
  updateHistoryRestoreProgress,
  endHistoryRestore,
  setInitialWhatsappDataHash,
  checkWhatsappHashChangeAndConfirm,
  confirmWhatsappHashChange,
  cancelWhatsappHashChange,
  resetWhatsappHashChanged,
  setTemplate,
  setTemplateConfigurationKey,
  updateWhatsappValidationStatuses,
  addTeaserTemplate,
  getTeaserForPopup,
  activateTeaser,
  activatePopupFromTeaser,
  deleteTeaser,
  prepareTeaserDataForBackend,
  cleanupWhatsappObservers,
  cleanupRcsObservers,
  fixMisplacedTranslations,
  syncGameFunnelSteps,
  updateTemplateConfigurationNextIdTemplate,
  afterSyncedGameWidgetBulkSave,
  propagateEmbedSettingsToLoadedFunnel
} from "@/composables/template-editor/TemplateEditor";
import { EDITOR_MOBILE_DEVICES } from "@/configs/editor-devices";

import EditorModal from "@/components/modals/EditorModal.vue";
import {
  ActiveLiveEditorItemData,
  PrimvueMenuModel,
  SelectedSyncElement,
  SyncElementWarning,
  TemplatePart
} from "@/types";
import { useI18n } from "vue-i18n";
import {
  getTemplateIframeDocument,
  getTemplateParentTypeByType,
  isDisplayTemplate,
  isEmailTemplate,
  isFacebookTemplate,
  isPushTemplate,
  isSmsTemplate,
  clearPlaceholder,
  showEditSyncElementWarning,
  getSectionParent,
  isRichTextingTemplate,
  isWhatsappTemplate,
  getWidgetTypeFromTemplate,
  getElementFromIframe,
  getWidgetType,
  getWidgetLabelTranslationKey,
  generateUniqStructureId
} from "@/components/template-builder/utils/helpers";
import {
  showToastError,
  showToastSuccess,
  generateSha1HashWithSalt,
  asInt,
  truncateLabel
} from "@/helpers";
import { getShopDefaultLang, UserState } from "@/composables/User";
import {
  checkIfSavePointNeeded,
  createHistory,
  getClosestPointOfSave,
  getHistoryIndexFromGroupId,
  getLastPointOfSaveId,
  getTemplateHistoryGroupList,
  getTemplateHistoryGroupListPaginated,
  loadHistoryGroups,
  restoreToHistoryPoint,
  HISTORY_PAGE_SIZE,
  markTemplateAsChanged
} from "@/composables/template-editor/History";
import {
  TemplatesElementsHistoryGroup,
  TemplatesParts
} from "@/types/generated-types/graphql";
import { useConfirm } from "primevue/useconfirm";
import ConfirmDialog from "primevue/confirmdialog";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Menu from "primevue/menu";
import ProgressSpinner from "primevue/progressspinner";
import Divider from "primevue/divider";
import {
  EditingTemplateStatusEnum,
  getEditingTemplateFromCampaign,
  setEditingTemplateFromCampaign,
  setCampaignTemplateInformationFromBuilder,
  AutomatedScenarioState
} from "@/composables/AutomatedScenarios";
import { EditingTemplateStatusEnum as FlowEditingTemplateStatusEnum } from "@/store/modules/flow-editor";
import TemplateSelectorV2 from "@/components/automated-scenarios/fields/TemplateSelectorV2.vue";
import { useTemplatesApi } from "@/composables/templates/useTemplatesApi";
import SendTestModal, {
  Suggestion
} from "@/components/modals/SendTestModal.vue";
import SendTestV4Modal from "@/components/modals/SendTestV4Modal.vue";
import PreviewDisplayV4Modal from "@/components/modals/PreviewDisplayV4Modal.vue";
import MigrateV4Modal from "@/components/modals/MigrateV4Modal.vue";
import {
  destroyAllSortables,
  destroyIframeSortables,
  resetListeners,
  trackMousePosition,
  stopScrolling
} from "@/components/template-builder/utils/listeners";
import { refreshSmartProductList } from "@/components/template-builder/callbacks";
import { removeActiveItemElementDecorations } from "@/components/template-builder/utils/active-item-builder";
import { htmlHasVoucher } from '@/components/template-builder/utils/voucher-detection';
import { initIconPositioning, cleanupIconPositioning } from "@/components/template-builder/utils/icon-positioning";
import { initDOMLabels, cleanupDOMLabels } from "@/components/template-builder/utils/dom-labels";
import { cleanupAllCountdownIntervals } from "@/components/template-builder/setters";
import { cleanupAllEditorBackgroundBindings } from "@/composables/useEditorBackground";
import DuplicateModal from "@/components/modals/DuplicateModal.vue";
import { NestApiType, nestCall } from "@/composables/nestApi";
import { isDemoModeActive, demoOverrides } from "@/composables/DemoMode";
import { getLocalStorageElement } from "@/helpers/LocalStorage";
import getStructurePanelConfigMap from "@/components/template-builder/config/structure-panel-config";
import ChooseZipFileModal from "@/components/modals/ChooseZipFileModal.vue";
import Loader from "@/components/layout/Loader.vue";
import { delay } from "@/helpers/helpers";
import HelpMessage from "@/components/common/HelpMessage.vue";
import { WhatsappValidationStatus } from "@/composables/flows/useWhatsappTemplateValidation";
import { SubmitWhatsappValidation } from "@/composables/services/Whatsapp";
import { getNotConfiguredServices } from "@/composables/shop/MyShopParameters";
import SyncElementNotUsedTemplates from "@/components/template-builder/SyncElementNotUsedTemplates.vue";
import SpmBadge from "@/components/table-rest-api/SpmBadge.vue";
import {
  updateIframeTranslations,
  fixMissingTranslations
} from "./utils/translate";
import { duplicateElement } from "./utils/action-buttons-handlers";
import { cleanupDynamicStyle } from "./utils/parser";
import { updateSyncElementsInOpenedTemplates } from "./utils/sync-elements-helpers";
import { isPanelVisible } from "./config/sync-elements-config";
import { isPopupTemplate } from "./utils/helpers";
import { TemplateTypeEnum } from "@/composables/shop/Templates";

export default defineComponent({
  name: "TemplateEditor",

  components: {
    HelpMessage,
    DuplicateModal,
    TemplateSelectorV2,
    SendTestModal,
    SendTestV4Modal,
    PreviewDisplayV4Modal,
    MigrateV4Modal,
    EditorModal,
    ConfigurationPanel,
    InformationPanel,
    TranslationPanel,
    HistoryPanel,
    HistoryRestoreLoader,
    CssPanel,
    CssPanelMediaQueries,
    LoadElementsPanel,
    TemplateLiveView,
    InputTextModal,
    SaveElementModal,
    WidgetsPanel,
    ConfirmDialog,
    ColumnWidgetModal,
    LoadElementsModal,
    SearchElementsModal,
    ChooseZipFileModal,
    Dialog,
    FunnelStep,
    SyncElementUsedTemplates,
    ActiveCampaignsList,
    Button,
    Menu,
    ProgressSpinner,
    Divider,
    Loader,
    SyncElementNotUsedTemplates,
    SpmBadge
  },

  setup() {
    const { t, locale } = useI18n();
    const idShop: number = UserState.activeShop ? UserState.activeShop.id : 0;

    const store = useStore();
    const confirm = useConfirm();

    // Variables pour stocker les données du conflit de version
    const currentConflictMessage = ref<string>("");
    const currentConflictLocalDate = ref<string>("");
    const currentConflictRemoteDate = ref<string>("");

    /* Teaser button logic */
    const isOnTeaser = computed(
      () => state.template?.type === TemplateTypeEnum.TEASER
    );

    const selectedTemplateId = computed(() => state.template?.id ?? 0);
    const selectedTemplateType = computed(() => state.template?.type ?? "");
    const selectedSyncElement = computed<SelectedSyncElement>(
      () => store.getters["liveEditor/getSelectedSyncElement"]
    );
    const selectedTemplateName = computed(() => {
      if (selectedSyncElement.value?.elementName) {
        return selectedSyncElement.value.elementName;
      }
      if (isOnTeaser.value) {
        return t("templateBuilder.teaser.teaser");
      }
      return state.template?.informations.name;
    });
    const selectedTemplateIdCampaign = computed(
      () => state.template?.idCampaign ?? null
    );
    const selectedStructure = computed(
      () => store.getters["liveEditor/getSelectedStructure"]
    );
    const syncElementWarning = computed<SyncElementWarning>(
      () => store.getters["liveEditor/getSyncElementWarning"]
    );
    const exitModeModalVisible = computed<boolean>(
      () => store.getters["liveEditor/getExitEditModeModalVisible"]
    );
    const exitModeModalCallback = computed<Function | null>(
      () => store.getters["liveEditor/getExitEditModeCallback"]
    );
    const displayElementsModal: ComputedRef<boolean> = computed(
      () => state.isLoadElementsModalVisible
    );

    // Key to force WidgetsPanel refresh when template changes
    const widgetsPanelKey = computed(
      () =>
        `widgets-${state.template?.sections?.length || 0}-${JSON.stringify(
          state.template?.sections?.map(s => s.html?.length) || []
        )}`
    );
    const subtitle = computed(() => {
      if (selectedSyncElement.value) {
        const modelName = state.template?.informations.name || "";

        if (selectedSyncElement.value.elementPartType) {
          const elementPartTypeTranslation = t(
            `templateBuilder.toolbar.header.subtitle.elementPartTypes.${selectedSyncElement.value.elementType}`
          );
          return t(
            "templateBuilder.toolbar.header.subtitle.editElementPartWithModel",
            [elementPartTypeTranslation, modelName]
          );
        }
        const elementTypeTranslation = t(
          `templateBuilder.toolbar.header.subtitle.elementTypes.${selectedSyncElement.value.elementType}`
        );
        return t(
          "templateBuilder.toolbar.header.subtitle.editElementWithModel",
          [elementTypeTranslation, modelName]
        );
      }
      return "";
    });
    const selectedTemplateConfiguration = computed(
      () => state.template?.configuration ?? {}
    );

    const structurePanelConfigMap = getStructurePanelConfigMap();
    const selectedStructureActions = computed(() => {
      if (
        isDisplayTemplate(selectedTemplateType.value) ||
        isEmailTemplate(selectedTemplateType.value)
      ) {
        const structureType = selectedStructure.value?.type;
        if (!structureType) return [];
        const structureTypeConfig = structurePanelConfigMap[structureType];
        if (!structureTypeConfig) return [];
        return structureTypeConfig.actions;
      }
      return [];
    });

    const isConfigurationPanelVisible = computed(
      () => state.isConfigurationPanelVisible
    );
    const isConfigurationPanelClosable = computed(() => {
      if (
        selectedTemplateType.value !== TemplateParentTypeEnum.SMS &&
        selectedTemplateType.value !== TemplateParentTypeEnum.PUSHNOTIFICATIONS
      ) {
        if (
          selectedSyncElement.value &&
          [
            TemplateStructureEnum.WIDGET,
            TemplateStructureEnum.SECTION
          ].includes(
            selectedSyncElement.value.elementType as TemplateStructureEnum
          )
        ) {
          return false;
        }
        return true;
      }
      return false;
    });
    const isLeftToolbarVisible = computed(() => {
      let returnValue = false;
      if (
        state.isLeftToolbarVisible &&
        (state.leftToolbar.show.configurationPanel ||
          state.leftToolbar.show.designPanel) &&
        selectedStructure.value
      ) {
        returnValue = true;
      } else if (
        state.isLeftToolbarVisible &&
        state.leftToolbar.show.configurationPanel &&
        !selectedStructure.value
      ) {
        returnValue = true;
      } else if (
        state.isLeftToolbarVisible &&
        !state.leftToolbar.show.configurationPanel &&
        !state.leftToolbar.show.designPanel
      ) {
        returnValue = true;
      }

      return returnValue;
    });
    const isWidgetPanelClosable = computed(() => {
      if (
        selectedTemplateType.value === TemplateParentTypeEnum.EMAIL &&
        !selectedSyncElement.value
      ) {
        return false;
      }
      if (
        selectedTemplateType.value === TemplateParentTypeEnum.EMAIL &&
        selectedSyncElement.value &&
        selectedSyncElement.value.elementType !== TemplateStructureEnum.SECTION
      ) {
        return true;
      }
      return true;
    });
    const isTranslationPanelClosable = computed(
      () => !state.template?.informations.imported
    );
    const advancedModeEnabled = computed(() => {
      if (state.advancedModeEnabled) {
        return true;
      }
      if (
        selectedSyncElement.value &&
        isPanelVisible(
          selectedSyncElement.value.elementType,
          "advancedCssPanel",
          selectedSyncElement.value.elementPartType
        )
      ) {
        return true;
      }
      return false;
    });
    const showTemplateSelector = ref(false);
    const showDuplicationModal = ref(false);
    const showSaveToMyTemplatesModal = ref(false);
    const saveToMyTemplatesName = ref("");
    const displaySyncElementTemplatesModal = ref(false);
    const displaySaveSyncElementTemplatesModal = ref(false);
    const displayUnlinkSyncElementModal = ref(false);

    const isChooseZipFileModalVisible = computed(
      () => store.getters["liveEditor/getIsChooseZipFileModalVisible"]
    );
    const isSendTestModalVisible = computed(
      () => store.state.general.sendTestModalVisible
    );
    const isSendTestEmailV4ModalVisible = computed(
      () => store.state.general.sendTestEmailV4ModalVisible
    );
    const isPreviewDisplayV4ModalVisible = computed(
      () => store.state.general.previewDisplayV4ModalVisible
    );
    const isMigrateV4ModalVisible = computed(
      () => store.state.general.migrateV4ModalVisible
    );

    const width = computed({
      get() {
        return state.templateWidth;
      },
      set(newWidth: string) {
        setTemplateWidth(newWidth);
      }
    });

    const historyList: Ref<TemplatesElementsHistoryGroup[]> = ref([]);
    const selectedHistory: Ref<TemplatesElementsHistoryGroup | null> = ref(
      null
    );
    const historyHasMore = ref(false);
    const historyIsLoadingMore = ref(false);
    const historyTotalItems = ref(0);
    const lastHistoryGroupId: ComputedRef<number | null> = computed(
      () => state.template?.configuration.lastIdTemplateElementsHistoryGroup
    );
    const currentHistoryGroupId: ComputedRef<number | null> = computed(
      () =>
        state.template?.configuration.currentIdTemplateElementsHistoryGroup ??
        lastHistoryGroupId.value
    );
    const firstHistoryGroupId: ComputedRef<number | null> = computed(() => {
      let lastHistoryPoint: TemplatesElementsHistoryGroup | null = null;

      if (historyList.value.length > 0) {
        lastHistoryPoint = historyList.value[historyList.value.length - 1];
      }

      return lastHistoryPoint?.id_template_elements_history_group ?? null;
    });

    // key used to rerender live preview on history change
    const livePreviewKey = ref(0);

    // flag to preserve current panel when restoring history
    const isRestoringHistory = ref(false);

    const displayRenameTemplateModal: Ref<boolean> = ref(false);
    const valueRenameTemplateModal: Ref<string> = ref("");

    const displaySaveElementModal: ComputedRef<boolean> = computed(
      () => store.state.liveEditor.saveElementModalPayload.visible
    );
    const typeSaveElementModal: ComputedRef<TemplateStructureEnum | null> = computed(
      () => store.state.liveEditor.saveElementModalPayload.type
    );

    const nameSaveElementModal: Ref<string> = ref("");
    const syncStateSaveElementModal: Ref<boolean> = ref(false);
    const saveElementModalRef: Ref<any> = ref(null);

    // Search Elements Modal variables
    const displaySearchElementsModal: Ref<boolean> = ref(false);
    const searchElementsType: Ref<string> = ref("");
    const searchElementsPartType: Ref<string> = ref("");
    const searchElementsSubtype: Ref<string> = ref("");
    const includeAllElementsPartType: Ref<boolean> = ref(false);

    const informationPanelValidation = ref<any>({});
    const newListName = ref();

    const syncElementUsedTemplatesTotal = ref(0);
    const syncElementUsedTemplatesLoaded = ref(false);
    const syncElementNotUsedTemplatesTotal = ref(0);
    const selectedNotUsedTemplates = ref<number[]>([]);
    const selectedNotUsedTemplatesSections = ref<Record<number, string[]>>({});
    const syncElementNotUsedTemplatesRef = ref();

    const syncElementUsedTemplatesRef = ref();
    const syncElementUsedTemplatesForSaveRef = ref();
    const displayActiveCampaignsWarningModal = ref(false);
    const activeCampaignsForWarning = ref<any[]>([]);

    /* Check if this template was edited from a campaign (old or new flow system) */
    const openingFromCampaign: ComputedRef<boolean> = computed(() => {
      // Check old system
      const legacyEditing = getEditingTemplateFromCampaign() !== null;
      // Check new flow system
      const flowEditing =
        store.getters["flowEditor/getEditingTemplate"] !== null;
      return (
        selectedTemplateIdCampaign.value !== null &&
        (legacyEditing || flowEditing)
      );
    });

    const isSubmittingWhatsapp = ref(false);

    const setLastHistory = (historyGroupId: number | null) => {
      if (historyGroupId) {
        const currentHistory: TemplatesElementsHistoryGroup[] = historyList.value.filter(
          (history: TemplatesElementsHistoryGroup) =>
            history.id_template_elements_history_group === historyGroupId
        );

        if (currentHistory.length) {
          [selectedHistory.value] = currentHistory;
        }
      }
    };

    /* Funnel steps */
    const getPopupTemplate = () => {
      // If on teaser, get the parent popup template
      if (
        state.template?.type === TemplateTypeEnum.TEASER &&
        state.template.originTemplateId
      ) {
        const popupId = state.template.originTemplateId;
        return state.templates.find(t => String(t.id) === popupId)
          || state.templatesLinkedToCampaign.find(t => String(t.id) === popupId)
          || null;
      }
      return state.template;
    };
    const funnels = computed(() => {
      const popup = getPopupTemplate();
      return popup?.funnel?.nextSteps || [];
    });
    const funnelStepRoot = computed(() => {
      const popup = getPopupTemplate();
      return popup?.funnel;
    });
    const funnelStepKey = computed(() => state.funnelStepKey);

    // Check if the root template of a funnel is a popup
    const isRootTemplatePopup = computed(() => {
      if (!state.template) return false;
      
      // If current template is already a popup, it's valid
      if (isPopupTemplate(state.template.type)) {
        // But check if it's a funnel step - if so, verify the root is also a popup
        const rootTemplateId = state.template.configuration?.root_template_id;
        if (rootTemplateId) {
          // Search in both templates and templatesLinkedToCampaign (for flow editor)
          const rootTemplate = state.templates.find(t => t.id === parseInt(rootTemplateId as string, 10))
            || state.templatesLinkedToCampaign.find(t => t.id === parseInt(rootTemplateId as string, 10));
          // If root template found, check its type; if not found, assume it's a popup
          // (since current template is popup and part of a funnel, root is likely also popup)
          return rootTemplate ? isPopupTemplate(rootTemplate.type) : true;
        }
        return true;
      }
      
      return false;
    });

    const canShowTeaserButton = computed(() => {
      // Show teaser section if we're on a popup (with popup root) or on a teaser
      return isRootTemplatePopup.value || isOnTeaser.value;
    });

    const canShowAddTeaserButton = computed(() => {
      if (!isRootTemplatePopup.value) return false;
      // Check if a teaser already exists for this popup or root popup (when on funnel step)
      const popupId =
        state.template?.originTemplateId || String(state.template?.id);
      return !state.teaserTemplates[popupId];
    });

    const canShowGoToTeaserButton = computed(() => {
      if (!isRootTemplatePopup.value) return false;
      // Check if a teaser exists for this popup or root popup (when on funnel step)
      const popupId = state.template?.originTemplateId
        ? parseInt(state.template.originTemplateId, 10)
        : state.template?.id;
      return popupId ? getTeaserForPopup(popupId) !== null : false;
    });

    const handleAddTeaser = () => {
      if (!state.template) return;

      // Resolve the root popup: use originTemplateId (set on all funnel children including game steps)
      let parentTemplate = state.template;
      const originId = state.template.originTemplateId;

      if (originId && String(state.template.id) !== originId) {
        const rootTemplate = state.templates.find(t => String(t.id) === originId)
          || state.templatesLinkedToCampaign.find(t => String(t.id) === originId);
        if (rootTemplate) {
          parentTemplate = rootTemplate;
        }
      }

      addTeaserTemplate(
        parentTemplate.id,
        parentTemplate.informations.name,
        parentTemplate.informations.lang || "fr"
      );

      destroyIframeSortables();
      livePreviewKey.value += 1;
    };

    const handleGoToTeaser = () => {
      if (!state.template) return;
      // Use originTemplateId (root popup) when on funnel step
      const popupId = state.template.originTemplateId
        ? parseInt(state.template.originTemplateId, 10)
        : state.template.id;
      activateTeaser(popupId);
      destroyIframeSortables();
      livePreviewKey.value += 1;
    };

    const handleGoToPopup = () => {
      activatePopupFromTeaser();
      destroyIframeSortables();
      livePreviewKey.value += 1;
    };

    // Teaser menu
    const teaserMenuRef = ref();
    const toggleTeaserMenu = (event: Event) => {
      teaserMenuRef.value?.toggle(event);
    };

    const handleDeleteTeaser = () => {
      if (!state.template) return;
      const wasOnTeaser = isOnTeaser.value;
      const popupId = state.template.originTemplateId
        ? parseInt(state.template.originTemplateId, 10)
        : state.template.id;
      deleteTeaser(popupId);

      // If we were on the teaser, switch to popup
      if (wasOnTeaser) {
        activatePopupFromTeaser();
      }

      // Refresh the view
      destroyIframeSortables();
      livePreviewKey.value += 1;
    };

    const teaserMenuItems = computed(() => [
      {
        label: t("templateBuilder.teaser.deleteTeaser"),
        icon: "far fa-trash",
        command: handleDeleteTeaser,
        class: "teaser-menu-item-delete"
      }
    ]);

    const handleChangeHistory = async (index: number) => {
      store.commit("liveEditor/setCheckSyncElements", true); // Set this to make sure sync elements are checked in case of update
      isRestoringHistory.value = true;
      startHistoryRestore(1);
      try {
        await restoreToHistoryPoint(
          historyList.value,
          index,
          (current, total, actionDetail) => {
            updateHistoryRestoreProgress(current, total, actionDetail);
          }
        );
        refreshSectionsConfig(
          JSON.parse(
            historyList.value[index].sections_order_after ?? ""
          ).map((id: string) => id.toString())
        );
        refreshTranslations();
        selectedHistory.value = historyList.value[index];
        setTemplateConfigurationKeyInState(
          "currentIdTemplateElementsHistoryGroup",
          selectedHistory.value.id_template_elements_history_group
        );
        setLastHistory(
          selectedHistory.value.id_template_elements_history_group
        );
        destroyIframeSortables();
        livePreviewKey.value += 1;
      } finally {
        endHistoryRestore();
      }
    };

    const handlePreviousHistory = () => {
      const currentIndex = historyList.value.findIndex(
        (current: TemplatesElementsHistoryGroup) =>
          current.id_template_elements_history_group ===
          selectedHistory.value?.id_template_elements_history_group
      );

      if (currentIndex === undefined) handleChangeHistory(0);
      // eslint-disable-next-line prefer-destructuring
      else if (currentIndex < historyList.value.length - 1)
        handleChangeHistory(currentIndex + 1);
    };

    const handleNextHistory = () => {
      const currentIndex = historyList.value.findIndex(
        (current: TemplatesElementsHistoryGroup) =>
          current.id_template_elements_history_group ===
          selectedHistory.value?.id_template_elements_history_group
      );
      if (currentIndex) handleChangeHistory(currentIndex - 1);
    };

    const handleLoadMoreHistory = async () => {
      if (historyIsLoadingMore.value || !historyHasMore.value) return;

      historyIsLoadingMore.value = true;
      try {
        const result = await getTemplateHistoryGroupListPaginated(
          historyList.value.length,
          HISTORY_PAGE_SIZE
        );
        historyList.value = [...historyList.value, ...result.items];
        historyHasMore.value = result.hasMore;
        historyTotalItems.value = result.total;
      } finally {
        historyIsLoadingMore.value = false;
      }
    };

    useToggleBodyScrollBars();

    const prepareStructureConfiguration = () => {
      store.dispatch("liveEditor/prepareStructureConfiguration", {
        type: TemplateStructureEnum.PAGE,
        identifier: TEMPLATE_PAGE_IDENTIFIER
      });
    };

    const getPageConfig = () => {
      if (
        isConfigurationPanelVisible.value &&
        selectedStructure.value?.type === TemplateStructureEnum.PAGE
      ) {
        hideConfigurationPanel();
        return;
      }
      prepareStructureConfiguration();
      showConfigurationPanel();
    };

    const handleSaveElementClose = () => {
      store.commit("liveEditor/hideSaveElementModal");
      nameSaveElementModal.value = "";
    };

    // Search Elements Modal handlers
    const handleSearchElements = (searchParams: {
      elementType: string;
      elementPartType: string;
      elementSubtype: string;
      includeAllElementsPartType: boolean;
    }) => {
      searchElementsType.value = searchParams.elementType;
      searchElementsPartType.value = searchParams.elementPartType;
      searchElementsSubtype.value = searchParams.elementSubtype;
      includeAllElementsPartType.value =
        searchParams.includeAllElementsPartType;
      displaySearchElementsModal.value = true;
    };

    const handleSearchElementsClose = () => {
      displaySearchElementsModal.value = false;
      searchElementsType.value = "";
      searchElementsPartType.value = "";
      searchElementsSubtype.value = "";
      includeAllElementsPartType.value = false;
    };

    const handleSearchElementSelected = (elementData?: {
      elementName: string;
      elementId: number;
    }) => {
      if (elementData) {
        // Set the selected element name in the save modal
        nameSaveElementModal.value = elementData.elementName;
        // Mark as selected from search in the next tick to ensure the modal is rendered
        nextTick(() => {
          if (
            saveElementModalRef.value &&
            saveElementModalRef.value.markAsSelectedFromSearch
          ) {
            saveElementModalRef.value.markAsSelectedFromSearch(
              elementData.elementId
            );
          }
        });
      }
      // Close the search modal
      handleSearchElementsClose();
    };

    const toEraseElementId = ref();
    const toEraseElementSyncId = ref("");
    const toEraseIsNewSync = ref(false);

    const updateOtherSyncElementsInTempalte = async (
      elementToDuplicate: HTMLElement | null,
      data: {
        syncElementId: string;
        elementId: string;
        elementType: string;
        elementPartType?: string;
        elementSubtype?: string;
        result?: any;
      }
    ) => {
      const otherSyncElementsToUpdate = getAllSyncElements(
        data.syncElementId ?? "",
        data.elementId
      );
      const itemData: ActiveLiveEditorItemData = {
        selector: `#${data.elementId}`,
        type: data.elementType as TemplateStructureEnum
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const otherSyncElementToUpdate of Array.from(
        otherSyncElementsToUpdate
      )) {
        if (elementToDuplicate) {
          // Duplicate if the template part is a widget or section itself
          if (!data.elementPartType) {
            // eslint-disable-next-line no-await-in-loop
            await duplicateElement(
              itemData,
              itemData.type,
              elementToDuplicate,
              null,
              null,
              null,
              null,
              null,
              false,
              `#${otherSyncElementToUpdate.id}`,
              otherSyncElementToUpdate.getAttribute("data-spmelementid") || ""
            );
          } else if (
            data.elementPartType === ElementPartTypeEnum.STYLES &&
            data.result
          ) {
            if (
              getWidgetTypeFromTemplate(data.elementSubtype || "") ===
              WidgetTypeEnum.SMART_PRODUCT_LIST
            ) {
              // eslint-disable-next-line no-await-in-loop
              await duplicateElement(
                itemData,
                itemData.type,
                elementToDuplicate,
                null,
                null,
                null,
                null,
                null,
                false,
                `#${otherSyncElementToUpdate.id}`,
                otherSyncElementToUpdate.getAttribute("data-spmelementid") ||
                  "",
                true
              );
            } else {
              // eslint-disable-next-line no-await-in-loop
              await insertSavedElement(
                data.result.newPart,
                `#${otherSyncElementToUpdate.id}`,
                "top",
                false,
                otherSyncElementToUpdate.getAttribute("data-spmelementid") || ""
              );
            }
          }
        }
      }
      // Remove sync elements if the part is not a whole element
      if (
        !data.elementPartType ||
        (data.elementPartType === ElementPartTypeEnum.STYLES &&
          getWidgetTypeFromTemplate(data.elementSubtype || "") ===
            WidgetTypeEnum.SMART_PRODUCT_LIST)
      ) {
        // eslint-disable-next-line no-restricted-syntax
        for (const otherSyncElementToUpdate of Array.from(
          otherSyncElementsToUpdate
        )) {
          otherSyncElementToUpdate.remove();
          removeCustomWidgetCss(`#${otherSyncElementToUpdate.id}`);
        }
      }
    };

    const updateSyncElementsInTemplate = async (data: {
      elementType: string;
      elementName: string;
      elementId: string;
      syncElementId: string;
      elementPartType?: string;
      elementSubtype?: string;
      result?: any;
    }) => {
      if (data.elementType !== TemplateStructureEnum.DESIGN) {
        const template = getTemplateIframeDocument();
        const elementToDuplicate: HTMLElement | null = template.querySelector(
          `#${data.elementId}`
        );
        if (elementToDuplicate) {
          elementToDuplicate.setAttribute(
            TEMPLATE_SYNC_ELEMENT_NAME,
            data.elementName || ""
          );
        }
        await updateOtherSyncElementsInTempalte(elementToDuplicate, data);
      } else {
        const template = getTemplateIframeDocument();
        if (template) {
          template.body.setAttribute(
            TEMPLATE_SYNC_ELEMENT_NAME,
            data.elementName ?? ""
          );
        }
      }

      await nextTick();

      if (data.result) {
        if (data.result.newPart.type === TemplateStructureEnum.SECTION) {
          const newElementData = JSON.parse(data.result.newPart.data);
          if (
            newElementData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] &&
            !newElementData[TEMPLATE_PART_ELEMENT_PART_TYPE] &&
            newElementData[TEMPLATE_SYNC_ELEMENT_IDs] &&
            Array.isArray(newElementData[TEMPLATE_SYNC_ELEMENT_IDs]) &&
            newElementData[TEMPLATE_SYNC_ELEMENT_IDs].length > 0
          ) {
            const syncWidgetIds = newElementData[TEMPLATE_SYNC_ELEMENT_IDs];
            // eslint-disable-next-line no-restricted-syntax
            for (const syncWidgetId of syncWidgetIds) {
              // eslint-disable-next-line no-await-in-loop
              const syncWidgetPart = await getTemplatePartsBySyncElementIds(
                idShop,
                [syncWidgetId]
              );
              if (syncWidgetPart.items && syncWidgetPart.items.length > 0) {
                const syncWidgetPartItem = syncWidgetPart.items[0];
                // eslint-disable-next-line no-await-in-loop
                await updateSyncElementsInOpenedTemplates(
                  state?.template?.id ?? 0,
                  syncWidgetPartItem
                );
                const template = getTemplateIframeDocument();
                if (template) {
                  let otherWidgets = Array.from(
                    template.querySelectorAll(
                      `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncWidgetId}"]`
                    )
                  );
                  if (otherWidgets.length > 0) {
                    otherWidgets = otherWidgets.filter(widget => {
                      const parentSection = widget.closest(
                        "[data-spmelementid]"
                      );
                      if (
                        parentSection &&
                        parentSection.hasAttribute(
                          TEMPLATE_SYNC_ELEMENT_IDENTIFIER
                        ) &&
                        !parentSection.hasAttribute(
                          TEMPLATE_PART_ELEMENT_PART_TYPE
                        )
                      ) {
                        return true;
                      }
                      return false;
                    });
                    // eslint-disable-next-line no-restricted-syntax
                    for (const otherWidget of otherWidgets) {
                      const syncWidgetData = JSON.parse(
                        syncWidgetPartItem.data || "{}"
                      );
                      // eslint-disable-next-line no-await-in-loop
                      await updateOtherSyncElementsInTempalte(
                        otherWidget as HTMLElement,
                        {
                          elementType: TemplateStructureEnum.WIDGET,
                          elementId: otherWidget.id,
                          syncElementId: syncWidgetId,
                          elementPartType:
                            syncWidgetData[TEMPLATE_PART_ELEMENT_PART_TYPE] ||
                            "",
                          elementSubtype:
                            syncWidgetData[TEMPLATE_PART_ELEMENT_SUBTYPE] || "",
                          result: {
                            newPart: syncWidgetPartItem
                          }
                        }
                      );
                    }
                  }
                }
              }
            }
          }
        }
        await updateSyncElementsInOpenedTemplates(
          state?.template?.id ?? 0,
          data.result.newPart
        );
      }

      cleanupDynamicStyle();
      removeActiveItemElementDecorations();
      updateSectionsInState(false);
      removeUnusedTranslations();
      refreshSectionsConfig();
      resetListeners();
      refreshSmartProductList();
    };

    const updateParentSection = async (
      syncParentSyncId: string,
      syncParentId: string
    ) => {
      if (syncParentSyncId && syncParentId) {
        const parentTemplatePart = await getTemplatePartsBySyncElementIds(
          idShop,
          [syncParentSyncId]
        );
        if (parentTemplatePart.items && parentTemplatePart.items.length > 0) {
          const parentTemplatePartItem = parentTemplatePart.items[0];
          const result = await savePartInDatabase(
            idShop,
            parentTemplatePartItem.type || "",
            parentTemplatePartItem.name || "",
            `#${syncParentId}`,
            asInt(parentTemplatePartItem.id_template_parts),
            true,
            syncParentSyncId
          );

          if (result && result.newPart) {
            await nestCall(
              "post",
              NestApiType.DEFAULT,
              "/template/updateSyncElements",
              {},
              "",
              {
                idShop,
                syncElementId: syncParentSyncId,
                elementType: TemplateStructureEnum.SECTION,
                elementId: syncParentId,
                idTemplate: state.template?.id,
                deleted: false
              }
            );

            await updateSyncElementsInTemplate({
              elementType: TemplateStructureEnum.SECTION,
              elementName: parentTemplatePartItem.name || "",
              elementId: syncParentId,
              syncElementId: syncParentSyncId,
              result
            });
          }
        }
      }
    };

    const saveElement = async (
      element: {
        name: string;
        type: string;
        isSync: boolean;
        renameElement?: {
          itemId: number;
          itemType: string;
          oldName: string;
          syncElementId?: string;
        };
        elementPartType?: ElementPartTypeEnum;
        fromSyncElement?: boolean;
        syncParentSyncId?: string;
        syncParentId?: string;
        selectedElementId?: number;
      },
      done: Function
    ) => {
      if (!element.renameElement) {
        const activeItemSelector = computed(
          () => store.getters["liveEditor/getSaveElementSelector"]
        );

        // Check if we have a selected element ID for overwrite (from search selection)
        let existingTemplatePart: any = null;
        if (element.selectedElementId) {
          try {
            existingTemplatePart = await getTemplatePartsById(
              element.selectedElementId
            );
          } catch {
            await showToastError(t("errorMessages.GENERIC_ERROR"));
            done(false, false);
            return;
          }
        }

        const updateElementInState = (syncId: string) => {
          const applyClassAndAttribute = (isDesign = false) => {
            const template = getTemplateIframeDocument();
            if (template) {
              let elementHtml = isDesign
                ? template.body
                : template.body.querySelector(activeItemSelector.value);
              if (isDisplayTemplate(state.template?.type) && isDesign) {
                elementHtml = template.querySelector("#spm_body");
              }
              if (elementHtml) {
                if (!element.fromSyncElement) {
                  elementHtml.setAttribute(
                    TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
                    syncId
                  );
                  elementHtml.setAttribute(
                    TEMPLATE_SYNC_ELEMENT_NAME,
                    element.name
                  );
                  if (
                    element.type === TemplateStructureEnum.SECTION &&
                    !element.elementPartType
                  ) {
                    const styleSyncChilds = elementHtml.querySelectorAll(
                      `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}="styles"]`
                    );
                    styleSyncChilds.forEach((styleSyncChild: HTMLElement) => {
                      if (
                        !styleSyncChild.hasAttribute(
                          TEMPLATE_PART_ELEMENT_IDENTIFIER
                        )
                      ) {
                        styleSyncChild.setAttribute(
                          TEMPLATE_PART_ELEMENT_IDENTIFIER,
                          generateUniqStructureId()
                        );
                      }
                    });
                  }
                }
                if (!isDesign && !element.elementPartType) {
                  elementHtml.classList.add(TEMPLATE_SYNC_ELEMENT_CLASS);
                  elementHtml.removeAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE);
                }
                if (element.elementPartType && !element.fromSyncElement) {
                  elementHtml.setAttribute(
                    TEMPLATE_PART_ELEMENT_PART_TYPE,
                    element.elementPartType
                  );
                  if (isEmailTemplate(state.template?.type)) {
                    const parentSection = getSectionParent(elementHtml);
                    if (
                      parentSection &&
                      parentSection.hasAttribute(
                        TEMPLATE_SYNC_ELEMENT_IDENTIFIER
                      ) &&
                      !parentSection.hasAttribute(
                        TEMPLATE_PART_ELEMENT_PART_TYPE
                      )
                    ) {
                      elementHtml.setAttribute(
                        TEMPLATE_PART_ELEMENT_IDENTIFIER,
                        generateUniqStructureId()
                      );
                    }
                  }
                }
              }
              return elementHtml;
            }
            return null;
          };

          let elementHtml: HTMLElement | null = null;

          switch (element.type) {
            case TemplateStructureEnum.SECTION:
              elementHtml = applyClassAndAttribute();
              if (elementHtml) {
                linkSection(
                  elementHtml.getAttribute("data-spmelementid"),
                  syncId,
                  element.name
                );
              }
              break;
            case TemplateStructureEnum.DESIGN:
              elementHtml = applyClassAndAttribute(true);
              linkSection(
                "",
                syncId,
                element.name,
                TemplateStructureEnum.DESIGN
              );
              break;
            default:
              elementHtml = applyClassAndAttribute();
              if (elementHtml) {
                const parentSection = getSectionParent(
                  elementHtml as HTMLElement
                );
                if (parentSection && !element.fromSyncElement) {
                  addNewSyncElementToSection(
                    parentSection.getAttribute("data-spmelementid"),
                    syncId
                  );
                }
              }
          }

          if (elementHtml) {
            return elementHtml.id;
          }
          return "";
        };

        const reset = (newSection: TemplatePart) => {
          store.commit("liveEditor/hideSaveElementModal");
          store.dispatch("templateEditor/updateSection", {
            section: newSection
          });
        };

        const htmlLooksLikeGameWidget = (html: string | undefined | null) => {
          if (!html) return false;
          return (
            html.includes("spm_widget_game_wheel")
            || html.includes("spm_widget_game_slot_machine")
            || html.includes("spm_widget_game_scratch_card")
          );
        };

        const getHtmlFromElementSaveReturn = (apiReturn: any): string =>
          apiReturn?.newSection?.html ?? apiReturn?.newPart?.html ?? "";

        const maybeSyncGameFunnelAfterGameWidgetElementSave = async (
          savedType: string,
          saveApiReturn: any | null | undefined
        ) => {
          if (savedType !== TemplateStructureEnum.WIDGET || !saveApiReturn) {
            return;
          }
          if (!htmlLooksLikeGameWidget(getHtmlFromElementSaveReturn(saveApiReturn))) {
            return;
          }
          // TheSpinner uses z-index 10_000_000 and covers the full viewport. Wait until
          // SaveElementModal is torn down (done + reset + Vue flush) before showing it, otherwise
          // clicks hit the loader overlay while the dialog is still visible.
          await nextTick();
          await nextTick();
          await nextTick();
          await new Promise<void>(resolve => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => resolve());
            });
          });
          store.commit("general/showTheSpinner");
          try {
            await nextTick();
            syncGameFunnelSteps();
            updateTemplateConfigurationNextIdTemplate();
          } finally {
            store.commit("general/hideTheSpinner");
          }
        };

        const schedulePostModalGameFunnelSync = (
          savedType: string,
          saveApiReturn: any | null | undefined
        ) => {
          void maybeSyncGameFunnelAfterGameWidgetElementSave(
            savedType,
            saveApiReturn
          ).catch(() => {});
        };

        const save = async (existingPart: any) => {
          let existingSyncId = "";
          if (existingPart && element.isSync) {
            const existingPartData = JSON.parse(existingPart.data || "{}");
            if (existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
              existingSyncId =
                existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];
            }
          }
          const existingPartId =
            existingPart && existingPart.id_template_parts
              ? existingPart.id_template_parts
              : null;
          let newPart: Record<string, any> | null = null;
          try {
            newPart =
              (await savePartInDatabase(
                idShop,
                element.type,
                element.name,
                activeItemSelector.value,
                existingPartId,
                element.isSync,
                existingSyncId,
                element.elementPartType
              )) ?? null;
          } catch (err) {
            await showToastError(t("errorMessages.GENERIC_ERROR"));
          }
          let elementId = "";
          if (newPart) {
            const { newSection, syncId } = newPart;
            if (element.isSync) {
              elementId = updateElementInState(syncId);
              updateSectionsInState(false);
              if (!element.elementPartType) {
                await hideLeftToolbar();
              } else if (
                element.elementPartType === ElementPartTypeEnum.STYLES &&
                selectedStructure.value
              ) {
                const structureConfigurationData: Record<string, any> = {
                  type: selectedStructure.value.type,
                  identifier: selectedStructure.value.identifier
                };
                if (
                  selectedStructure.value.type === TemplateStructureEnum.WIDGET
                ) {
                  const widgetType = getWidgetTypeFromTemplate(
                    selectedStructure.value.identifier
                  );
                  if (widgetType) {
                    structureConfigurationData.widgetType = widgetType;
                  }
                }
                store.commit("liveEditor/resetSelectedStructure");
                await nextTick();
                store.dispatch(
                  "liveEditor/prepareStructureConfiguration",
                  structureConfigurationData
                );
              }
            }
            if (element.syncParentSyncId && element.syncParentId) {
              await updateParentSection(
                element.syncParentSyncId,
                element.syncParentId
              );
            }
          }
          return { newPart, elementId };
        };

        if (
          typeof existingTemplatePart === "string" &&
          existingTemplatePart !== ""
        ) {
          // Show error
          await showToastError(t("errorMessages.GENERIC_ERROR"));
          done(false, false);
        } else if (existingTemplatePart) {
          toEraseElementId.value = existingTemplatePart.id_template_parts;
          const existingPartData = JSON.parse(
            existingTemplatePart.data || "{}"
          );
          if (existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]) {
            toEraseElementSyncId.value =
              existingPartData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];
          }
          if (element.isSync) {
            toEraseIsNewSync.value = true;
          }

          // Generate dynamic element label based on type and elementPartType
          const getElementLabel = () => {
            const partData = JSON.parse(existingTemplatePart.data || "{}");
            const partElementPartType =
              partData[TEMPLATE_PART_ELEMENT_PART_TYPE];
            const partElementSubtype = partData[TEMPLATE_PART_ELEMENT_SUBTYPE];

            if (partElementPartType) {
              const subtypeLabel = partElementSubtype
                ? t(
                    getWidgetLabelTranslationKey(
                      getWidgetType(partElementSubtype) as WidgetTypeEnum
                    )
                  )
                : "";
              return t(
                `templateBuilder.panels.loadElements.chip.elementPart.${partElementPartType}`,
                [
                  t(
                    `templateBuilder.panels.loadElements.chip.elementPart.elements.${existingTemplatePart.type}`,
                    [subtypeLabel]
                  )
                ]
              );
            }
            return t(
              `templateBuilder.panels.loadElements.chip.${existingTemplatePart.type}`
            );
          };

          // If used, we ask confirmation to erase the old one
          confirm.require({
            group: "saveElement",
            message: t(
              "templateBuilder.confirm.saveNewSection.duplicateName.message",
              [getElementLabel(), element.name]
            ),
            header: t(
              "templateBuilder.confirm.saveNewSection.duplicateName.header"
            ),
            icon: "far fa-exclamation-triangle",
            acceptLabel: t("yes"),
            acceptClass: "p-button-danger",
            rejectLabel: t("no"),
            rejectClass: "p-button-secondary",
            accept: async () => {
              let result: { newPart: any; elementId: string } | null = null;
              try {
                result = await save(existingTemplatePart);
              } catch {
                result = null;
                showToastError(t("errorMessages.GENERIC_ERROR"));
              }
              if (result) {
                try {
                  if (element.isSync) {
                    let newPartData;
                    let elementSubtype = null;
                    if (result.newPart) {
                      newPartData = JSON.parse(result.newPart.data || "{}");
                      if (newPartData[TEMPLATE_PART_ELEMENT_SUBTYPE]) {
                        elementSubtype =
                          newPartData[TEMPLATE_PART_ELEMENT_SUBTYPE];
                      }
                    }
                    await nestCall(
                      "post",
                      NestApiType.DEFAULT,
                      "/template/updateSyncElements",
                      {},
                      "",
                      {
                        idShop,
                        syncElementId: toEraseElementSyncId.value,
                        elementType: element.type,
                        elementId: "",
                        idTemplate: state.template?.id,
                        deleted: false,
                        initRedirect: "all"
                      }
                    );

                    await updateSyncElementsInTemplate({
                      elementType: element.type,
                      elementName: element.name,
                      elementId: result.elementId,
                      syncElementId: toEraseElementSyncId.value,
                      elementPartType: element.elementPartType,
                      elementSubtype,
                      result
                    });
                  } else {
                    await nestCall(
                      "post",
                      NestApiType.DEFAULT,
                      "/template/updateSyncElements",
                      {},
                      "",
                      {
                        idShop,
                        syncElementId: toEraseElementSyncId.value,
                        elementType: element.type,
                        idTemplate: state.template?.id,
                        deleted: true
                      }
                    );
                    await updateSyncElementsInOpenedTemplates(
                      state.template?.id ?? 0,
                      result.newPart as TemplatesParts,
                      true
                    );
                  }
                } catch {
                  await showToastError(t("errorMessages.GENERIC_ERROR"));
                  done(false, false);
                  return;
                }
              }
              done();
              toEraseElementId.value = null;
              toEraseElementSyncId.value = "";
              toEraseIsNewSync.value = false;
              nameSaveElementModal.value = "";
              await nextTick();
              if (result?.newPart?.newSection) {
                reset(result.newPart.newSection);
                await nextTick();
              }
              schedulePostModalGameFunnelSync(element.type, result?.newPart);
            },
            reject: () => {
              store.commit("liveEditor/hideSaveElementModal");
              toEraseElementId.value = null;
              toEraseElementSyncId.value = "";
              toEraseIsNewSync.value = false;
              nameSaveElementModal.value = "";
              done(true, false);
            }
          });
        } else {
          // No part with same name
          const saveResult = await save(null);
          done();
          nameSaveElementModal.value = "";
          syncStateSaveElementModal.value = false;
          await nextTick();
          if (saveResult?.newPart?.newSection) {
            reset(saveResult.newPart.newSection);
            await nextTick();
          }
          schedulePostModalGameFunnelSync(element.type, saveResult?.newPart);
        }
      } else {
        try {
          if (
            element.renameElement.oldName.toLowerCase().trim() !==
            element.name.toLowerCase().trim()
          ) {
            await renamePartInDatabase(
              element.renameElement.itemId,
              element.name
            );

            if (element.renameElement.syncElementId) {
              await nestCall(
                "post",
                NestApiType.DEFAULT,
                "/template/updateSyncElements",
                {},
                "",
                {
                  idShop,
                  syncElementId: element.renameElement.syncElementId,
                  elementType: element.renameElement.itemType,
                  idTemplate: state.template?.id,
                  deleted: false
                }
              );
              updateUsedSyncElementsName(
                element.renameElement.syncElementId || "",
                element.name
              );
              const newElement = await getTemplatePartsById(
                element.renameElement.itemId
              );
              if (newElement) {
                if (newElement.type === TemplateStructureEnum.SECTION) {
                  const newElementData = JSON.parse(newElement.data || "{}");
                  if (
                    newElementData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] &&
                    !newElementData[TEMPLATE_PART_ELEMENT_PART_TYPE] &&
                    newElementData[TEMPLATE_SYNC_ELEMENT_IDs] &&
                    Array.isArray(newElementData[TEMPLATE_SYNC_ELEMENT_IDs]) &&
                    newElementData[TEMPLATE_SYNC_ELEMENT_IDs].length > 0
                  ) {
                    const syncWidgetIds =
                      newElementData[TEMPLATE_SYNC_ELEMENT_IDs];
                    // eslint-disable-next-line no-restricted-syntax
                    for (const syncWidgetId of syncWidgetIds) {
                      // eslint-disable-next-line no-await-in-loop
                      const syncWidgetPart = await getTemplatePartsBySyncElementIds(
                        idShop,
                        [syncWidgetId]
                      );
                      if (
                        syncWidgetPart.items &&
                        syncWidgetPart.items.length > 0
                      ) {
                        const syncWidgetPartItem = syncWidgetPart.items[0];
                        // eslint-disable-next-line no-await-in-loop
                        await updateSyncElementsInOpenedTemplates(
                          state?.template?.id ?? 0,
                          syncWidgetPartItem
                        );
                      }
                    }
                  }
                }
                await updateSyncElementsInOpenedTemplates(
                  state?.template?.id ?? 0,
                  newElement
                );
              }
            }

            store.dispatch("templateEditor/updateSectionName", {
              id: element.renameElement.itemId,
              name: element.name
            });
            done();
            nameSaveElementModal.value = "";
            syncStateSaveElementModal.value = false;
          }
        } catch (error) {
          showToastError(t("errorMessages.GENERIC_ERROR"));
          done(false);
        }
      }
    };

    const sendTestTemplateV4 = async (
      payload: {
        params: Suggestion[] | Suggestion;
        channel?: 'email' | 'sms' | 'whatsapp' | 'richtexting';
        contactId: number | null;
        langs?: string[];
        conditionOverrides?: Record<string, boolean>;
        senderId?: string;
        rcsSenderId?: string;
        done?: () => void;
      },
    ) => {
      if (state.template) {
        const getData: Record<string, any> = {
          type: selectedTemplateType.value,
          params: payload.params,
        };

        if (payload.contactId) {
          getData.contactId = payload.contactId;
        }
        if (Array.isArray(payload.langs) && payload.langs.length > 0) {
          getData.langs = payload.langs;
        }
        if (payload.conditionOverrides && Object.keys(payload.conditionOverrides).length > 0) {
          getData.conditionOverrides = payload.conditionOverrides;
        }

        const resolvedRcsSenderId = payload.rcsSenderId || payload.senderId;
        if (isRichTextingTemplate(selectedTemplateType.value) && resolvedRcsSenderId) {
          getData.senderId = resolvedRcsSenderId;
          getData.rcsSenderId = resolvedRcsSenderId;
          getData.informations = { rcsSenderId: resolvedRcsSenderId };
        }

        const templateIdToTest = isWhatsappTemplate(selectedTemplateType.value) && state.template.originTemplateId
          ? parseInt(state.template.originTemplateId, 10)
          : state.template.id;
        const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
        const result = await nestCall(
          "get",
          NestApiType.DEFAULT,
          `/template/test/${idShop}/${templateIdToTest}/${dataQuery}`,
          {},
          "",
          {},
          false
        );

        if (result && result.success) {
          if (isSmsTemplate(selectedTemplateType.value) || payload.channel === 'sms') {
            await showToastSuccess("smsSent");
          } else if (isWhatsappTemplate(selectedTemplateType.value) || payload.channel === 'whatsapp') {
            await showToastSuccess("whatsappSent");
          } else if (isRichTextingTemplate(selectedTemplateType.value) || payload.channel === 'richtexting') {
            await showToastSuccess("testSent");
          } else {
            await showToastSuccess("emailSent");
          }
        } else {
          await showToastError(
            result && result.message
              ? t(`errorMessages.${result.message}`)
              : t("errorMessages.GENERIC_ERROR")
          );
        }
      }

      if (payload.done) payload.done();
    };

    const sendTestTemplate = async (params: Suggestion[]) => {
      if (state.template) {
        store.commit("general/showTheSpinner");

        // Send query to NestJS
        const getData = {
          type: selectedTemplateType.value,
          params
        };

        // For WhatsApp, always send the root template
        const templateIdToTest = isWhatsappTemplate(selectedTemplateType.value) && state.template.originTemplateId
          ? parseInt(state.template.originTemplateId, 10)
          : state.template.id;

        const dataQuery = btoa(encodeURIComponent(JSON.stringify(getData)));
        const result = await nestCall(
          "get",
          NestApiType.DEFAULT,
          `/template/test/${idShop}/${templateIdToTest}/${dataQuery}`,
          {},
          "",
          {},
          false
        );

        if (result && result.success) {
          if (isEmailTemplate(selectedTemplateType.value)) {
            await showToastSuccess("emailSent");
          } else if (isSmsTemplate(selectedTemplateType.value)) {
            await showToastSuccess("smsSent");
          }
        } else {
          await showToastError(
            result && result.message
              ? t(`errorMessages.${result.message}`)
              : t("errorMessages.GENERIC_ERROR")
          );
        }

        store.commit("general/hideTheSpinner");
      }
    };

    const executeBeforeTemplateSave = async () => {
      if (
        !isSmsTemplate(selectedTemplateType.value) &&
        !isPushTemplate(selectedTemplateType.value) &&
        !isRichTextingTemplate(selectedTemplateType.value) &&
        !isFacebookTemplate(selectedTemplateType.value) &&
        !isWhatsappTemplate(selectedTemplateType.value)
      ) {
        await resetTemplateToDesktop();
      }

      return beforeSaveTemplate(t);
    };

    // Function executer when the user click on the save & exit button or the save & stay button
    const executeTemplateSave = async (
      exit = false,
      treatSwitchToCampaign = true,
      validateInformationPanel = true,
      historyType = HistoryType.POINT_OF_SAVE
    ) => {
      store.commit("general/showTheSpinner");
      let validation: Record<string, any> = { success: true };

      if (validateInformationPanel) {
        // Validate InformationPanel values
        validation = await informationPanelSettingsValidate(true);
        informationPanelValidation.value = validation.validate;
      }

      if (!validation.success) {
        // Open InformationPanel to see errors
        if (
          !state.isLeftToolbarVisible ||
          !state.leftToolbar.show.informationPanel
        ) {
          await togglePanel("informationPanel");
        }
        store.commit("general/hideTheSpinner");
        return;
      }

      if (!(await executeBeforeTemplateSave())) {
        store.commit("general/hideTheSpinner");
        return;
      }

      // Update state - this prepares the template data
      updateSectionsInState(false);

      // Define the save action (after data is prepared)
      const performSave = async () => {
        await createHistory(historyType, true);

        const saved = await saveTemplate();
        if (saved) {
          // Set needRevalidation only if WhatsApp hash changed (for approved templates in scenario)
          if (
            openingFromCampaign.value &&
            isWhatsappTemplate(selectedTemplateType.value) &&
            state.whatsappHashChanged
          ) {
            store.commit("liveEditor/setNeedRevalidation", true);
          }
          if (exit) {
            if (openingFromCampaign.value && treatSwitchToCampaign) {
              const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
              if (
                editingTemplateFromCampaign !== null &&
                editingTemplateFromCampaign.status ===
                  EditingTemplateStatusEnum.IN_TEMPLATE_EDITOR
              ) {
                editingTemplateFromCampaign.status =
                  EditingTemplateStatusEnum.SAVE_QUIT_TEMPLATE_EDITOR;
                setEditingTemplateFromCampaign(editingTemplateFromCampaign);
                setCampaignTemplateInformationFromBuilder(
                  state.template?.informations
                );
              }
            }
            // Update flowEditor store for new flow system (independent of old system)
            const flowEditingTemplate =
              store.getters["flowEditor/getEditingTemplate"];
            if (
              flowEditingTemplate !== null &&
              flowEditingTemplate.status ===
                FlowEditingTemplateStatusEnum.IN_TEMPLATE_EDITOR
            ) {
              store.commit("flowEditor/setEditingTemplate", {
                ...flowEditingTemplate,
                status: FlowEditingTemplateStatusEnum.SAVE_QUIT_TEMPLATE_EDITOR,
                informations: state.template?.informations
                  ? {
                      subject: state.template.informations.subject,
                      fromName: state.template.informations.fromName,
                      fromEmail: state.template.informations.fromEmail,
                      previewText: state.template.informations.previewText,
                    }
                  : undefined,
              });
            }
            deactivate(state.template?.id ?? 0);
          } else {
            // Refresh smart products lists and listeners
            await refreshSmartProductList();
            resetListeners();
          }
        }
        store.commit("general/hideTheSpinner");
      };

      // Check WhatsApp hash change AFTER data is prepared
      if (isWhatsappTemplate(selectedTemplateType.value)) {
        if (!checkWhatsappHashChangeAndConfirm(performSave)) {
          store.commit("general/hideTheSpinner");
          return; // Modal shown, waiting for confirmation
        }
      }

      // No confirmation needed, proceed directly
      await performSave();
    };

    const restoreTemplateBeforeEdit = () => {
      const currentState = store.getters["liveEditor/getCurrentTemplateState"];
      if (currentState) {
        restoreState(currentState);
        destroyIframeSortables();
        livePreviewKey.value += 1;
        store.commit("liveEditor/setCurrentTemplateState", null);
      }
    };

    const saveForAllInstancesLoading = ref(false);
    const unlinkSyncElementLoading = ref(false);

    const saveForAllInstances = async (campaignsMustBeDrafted: number[] = []) => {
      try {
        saveForAllInstancesLoading.value = true;

        const {
          elementType,
          elementName,
          elementId,
          syncElementId,
          elementPartType,
          elementSubtype
        } = selectedSyncElement.value;

        const templatePart = await getTemplatePartsBySyncElementIds(idShop, [
          syncElementId ?? ""
        ]);

        if (templatePart.items && templatePart.items.length) {
          // Check if element contains voucher and warn about active campaigns without hasVoucher config
          if (campaignsMustBeDrafted.length === 0) {
            // Get the element from iframe and check if it contains voucher
            const syncElementInFrame = getElementFromIframe(`#${elementId}`);
            if (syncElementInFrame && !elementPartType && elementType !== TemplateStructureEnum.DESIGN) {
              const elementHasVoucher = htmlHasVoucher(
                syncElementInFrame.outerHTML ?? '',
                state.template?.translations,
              );

              if (elementHasVoucher) {
                // Get templates from the ref and check which ones don't have hasVoucher=true
                const templatesData = syncElementUsedTemplatesForSaveRef.value?.templates?.items || [];

                // Filter templates with active campaigns that don't have hasVoucher=true in their config
                const activeCampaigns = templatesData.filter((item: any) => {
                  if (!item.id_campaign || item.status !== 'active') return false;

                  // Check if template_config has hasVoucher=true
                  const templateConfig = JSON.parse(item.template_config || '{}');
                  return templateConfig.hasVoucher !== true;
                });

                // Also check selected not-used templates from active campaigns without hasVoucher
                const notUsedWorkflows = syncElementNotUsedTemplatesRef.value?.fetchedWorkflows || [];
                const selectedTemplateIds = new Set(selectedNotUsedTemplates.value);

                // Find templates from active campaigns that are selected and don't have hasVoucher
                notUsedWorkflows.forEach((workflow: any) => {
                  if (workflow.status !== 'active') return;

                  const workflowTemplates = workflow.templates || [];
                  workflowTemplates.forEach((template: any) => {
                    if (!selectedTemplateIds.has(template.id_template)) return;

                    const templateConfig = JSON.parse(template.template_config || '{}');
                    if (templateConfig.hasVoucher !== true) {
                      // Add to activeCampaigns list with campaign info
                      activeCampaigns.push({
                        id_template: template.id_template,
                        label: template.label,
                        id_campaign: workflow.id_campaign_automation,
                        name_campaign_automation: workflow.name,
                        status: workflow.status,
                        template_config: template.template_config,
                      });
                    }
                  });
                });

                if (activeCampaigns.length > 0) {
                  // Show warning modal with active campaigns
                  activeCampaignsForWarning.value = activeCampaigns;
                  displayActiveCampaignsWarningModal.value = true;
                  saveForAllInstancesLoading.value = false;
                  return;
                }
              }
            }
          }

          const result = await savePartInDatabase(
            idShop,
            elementType,
            elementName ?? "",
            `#${elementId}`,
            asInt(templatePart.items[0].id_template_parts),
            true,
            syncElementId,
            elementPartType
          );

          if (
            selectedNotUsedTemplates.value.length &&
            (elementPartType === ElementPartTypeEnum.STYLES ||
              elementType === TemplateStructureEnum.DESIGN ||
              elementType === TemplateStructureEnum.SECTION)
          ) {
            const resultApply = await nestCall(
              "post",
              NestApiType.DEFAULT,
              "/template/applySyncElementToSelectedTemplates",
              {},
              "",
              {
                idShop,
                syncElementId,
                elementType,
                elementPartType,
                elementSubtype,
                selectedNotUsedTemplates: selectedNotUsedTemplates.value,
                selectedSectionsByTemplate:
                  selectedNotUsedTemplatesSections.value
              }
            );
            if (resultApply && !resultApply.success) {
              throw new Error(
                t(
                  "templateBuilder.saveSyncElementModal.error.saveForAllInstances"
                )
              );
            }
          }

          // For "save for all instances", redirections must be duplicated in all
          // templates consuming this synchronized template_part.
          const initRedirectValue = "all";

          await nestCall(
            "post",
            NestApiType.DEFAULT,
            "/template/updateSyncElements",
            {},
            "",
            {
              idShop,
              syncElementId,
              elementType,
              elementId,
              idTemplate: state.template?.id,
              deleted: false,
              initRedirect: initRedirectValue,
              campaignsMustBeDrafted
            }
          );

          await updateSyncElementsInTemplate({
            elementType,
            elementName: elementName ?? "",
            elementId,
            syncElementId: syncElementId ?? "",
            elementPartType,
            elementSubtype,
            result
          });

          await afterSyncedGameWidgetBulkSave({ elementType, elementId });

          store.commit("liveEditor/setSelectedSyncElement", null);
          store.commit("liveEditor/setCurrentTemplateState", null);

          await togglePanel("widgetsPanel");
          saveForAllInstancesLoading.value = false;
          displaySaveSyncElementTemplatesModal.value = false;

          // Reset selected templates after successful save
          resetNotUsedTemplatesSelection();

          showToastSuccess(
            t(
              "templateBuilder.saveSyncElementModal.success.saveForAllInstances"
            )
          );
          // If from edit mode
          if (exitModeModalCallback.value) {
            await delay(100);
            await exitModeModalCallback.value();
          }
          setRefreshTemplatesList(true);
        } else {
          saveForAllInstancesLoading.value = false;
          throw new Error(
            t("templateBuilder.saveSyncElementModal.error.saveForAllInstances")
          );
        }
      } catch (err) {
        saveForAllInstancesLoading.value = false;
        showToastError(
          t("templateBuilder.saveSyncElementModal.error.saveForAllInstances")
        );
      }
    };

    const handleSaveForAllInstances = async () => {
      if (
        selectedSyncElement.value &&
        selectedSyncElement.value.elementType ===
          TemplateStructureEnum.SECTION &&
        !selectedSyncElement.value.elementPartType
      ) {
        const syncElementInFrame = getElementFromIframe(
          `#${selectedSyncElement.value.elementId}`
        );
        if (syncElementInFrame) {
          const syncStyleWidgets = Array.from(
            syncElementInFrame.querySelectorAll(
              `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}="styles"]`
            )
          );
          if (syncStyleWidgets.length > 0) {
            confirm.require({
              group: "syncedElements",
              header: t(
                "templateBuilder.saveSyncElementModal.confirmSaveWithStylesSync.title"
              ),
              message: t(
                "templateBuilder.saveSyncElementModal.confirmSaveWithStylesSync.message",
                { count: syncStyleWidgets.length }
              ),
              acceptLabel: t("yes"),
              rejectLabel: t("no"),
              accept: async () => {
                await saveForAllInstances();
              }
            });
            return;
          }
        }
      }
      await saveForAllInstances();
    };

    // Confirm save when there are active campaigns with new vouchers
    const confirmSaveWithActiveCampaigns = async () => {
      // Extract campaign IDs from the active campaigns
      const campaignIds = activeCampaignsForWarning.value
        .map((campaign: any) => campaign.id_campaign)
        .filter((id: number | null) => id !== null);
      displayActiveCampaignsWarningModal.value = false;
      await saveForAllInstances(campaignIds);
    };

    // Command in edit mode
    const commandSaveForAllInstances = async () => {
      // Close the translation panel before opening the save modal so the iframe DOM
      // is restored to the default language (handled by the existing translationPanel
      // watcher). Otherwise syncTranslationsFromDOM during save would propagate the
      // currently-displayed non-default language values to all languages in the state.
      if (state.leftToolbar.show.translationPanel) {
        await togglePanel("widgetsPanel");
      }

      const validation = await informationPanelSettingsValidate(
        true,
        selectedSyncElement.value.elementName
      );
      informationPanelValidation.value = validation.validate;

      if (!validation.success) {
        // Open InformationPanel to see errors
        if (
          !state.isLeftToolbarVisible ||
          state.leftToolbar.show.informationPanel === false
        ) {
          await togglePanel("informationPanel");
        }
      } else {
        displaySaveSyncElementTemplatesModal.value = true;
      }
    };

    const showUnlinkAndSaveModal = async () => {
      // Same rationale as commandSaveForAllInstances: close translation panel first.
      if (state.leftToolbar.show.translationPanel) {
        await togglePanel("widgetsPanel");
      }
      displayUnlinkSyncElementModal.value = true;
    };

    const commandUnlinkAndSave = async () => {
      const bulkGameWidgetCtx = selectedSyncElement.value
        ? {
            elementType: selectedSyncElement.value.elementType,
            elementId: selectedSyncElement.value.elementId
          }
        : null;
      try {
        if (syncElementUsedTemplatesRef.value && state.template) {
          const selectedRows =
            syncElementUsedTemplatesRef.value.getSelectedRows() || [];
          const currentTemplate = selectedRows.find(
            (row: any) => row === state.template?.id
          );
          const openedTemplates = state.templates
            .filter(templateItem => selectedRows.includes(templateItem.id))
            .map(templateItem => templateItem.id);
          const selectedTemplates = selectedRows
            .filter((row: any) => row !== state.template?.id)
            .filter((row: any) => !openedTemplates.includes(row));
          if (selectedTemplates.length) {
            const resultUnlink = await nestCall(
              "post",
              NestApiType.DEFAULT,
              "/template/unlinkSyncElementsInTemplates",
              {},
              "",
              {
                idShop,
                syncElementId: selectedSyncElement.value.syncElementId,
                selectedTemplates
              }
            );
            if (resultUnlink && !resultUnlink.success) {
              throw new Error(
                t("templateBuilder.saveSyncElementModal.error.unlinkAndSave")
              );
            }
          }
          if (openedTemplates.length) {
            const syncElementData = JSON.stringify({
              [TEMPLATE_SYNC_ELEMENT_IDENTIFIER]:
                selectedSyncElement.value.syncElementId
            });
            await updateSyncElementsInOpenedTemplates(
              state?.template?.id ?? 0,
              { data: syncElementData },
              true,
              openedTemplates
            );
          }
          if (currentTemplate) {
            unlinkSyncElementLoading.value = true;
            unlinkSyncElement(selectedSyncElement.value);
          }
          removeActiveItemElementDecorations();
          updateSectionsInState(false);
          if (bulkGameWidgetCtx) {
            await afterSyncedGameWidgetBulkSave(bulkGameWidgetCtx);
          }
          if (
            isEmailTemplate(state.template?.type) &&
            selectedSyncElement.value.elementType ===
              TemplateStructureEnum.WIDGET
          ) {
            const selectedSyncElementInIframe = getElementFromIframe(
              `#${selectedSyncElement.value.elementId}`
            );
            if (selectedSyncElementInIframe) {
              const sectionParent = getSectionParent(
                selectedSyncElementInIframe
              );
              if (
                sectionParent &&
                sectionParent.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) &&
                !sectionParent.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
              ) {
                await updateParentSection(
                  sectionParent.getAttribute(
                    TEMPLATE_SYNC_ELEMENT_IDENTIFIER
                  ) || "",
                  sectionParent.id
                );
              }
            }
          }
          store.commit("liveEditor/setSelectedSyncElement", null);
          store.commit("liveEditor/setExitEditModeModalVisible", false);
          await togglePanel("widgetsPanel");
          if (exitModeModalCallback.value) {
            await delay(100);
            await exitModeModalCallback.value();
          }
          displayUnlinkSyncElementModal.value = false;
          unlinkSyncElementLoading.value = false;
          showToastSuccess(
            t("templateBuilder.saveSyncElementModal.success.unlinkAndSave")
          );
        }
      } catch (error) {
        showToastError(
          t("templateBuilder.saveSyncElementModal.error.unlinkAndSave")
        );
      }
    };
    const commandCancelModifications = async () => {
      confirm.require({
        group: "syncedElements",
        message: t("templateBuilder.confirm.cancelEditSyncElement.message"),
        header: t("templateBuilder.confirm.cancelEditSyncElement.header"),
        icon: "far fa-exclamation-triangle",
        acceptLabel: t("yes"),
        acceptClass: "p-button-danger",
        rejectLabel: t("no"),
        rejectClass: "p-button-secondary",
        accept: async () => {
          store.commit("liveEditor/setExitEditModeModalVisible", false);
          removeActiveItemElementDecorations();
          store.commit("liveEditor/setSelectedSyncElement", null);
          await togglePanel("widgetsPanel");
          restoreTemplateBeforeEdit();
        }
      });
    };

    const saveAndLeaveCommand = async () => {
      store.commit("liveEditor/setFormWidgetShowLabel", false);
      store.commit("liveEditor/setIsExitCurrentChangesModalVisible", false);
      const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
      let primaryTemplateToSwitch: any = null;

      if (openingFromCampaign.value && editingTemplateFromCampaign) {
        const templatesLinkedToThisCampaign = state.templatesLinkedToCampaign.filter(
          (c: any) => c.idCampaign === editingTemplateFromCampaign.id_campaign
        );
        const idTemplatesLinkedToThisCampaign = templatesLinkedToThisCampaign.map(
          (c: any) => c.id
        );
        const primaryTemplate = editingTemplateFromCampaign.idTemplate;

        // on est dans un template de redirection
        if (
          idTemplatesLinkedToThisCampaign.includes(state.template?.id) &&
          state.template?.id !== primaryTemplate
        ) {
          primaryTemplateToSwitch = state.templatesLinkedToCampaign.filter(
            (c: any) => c.id === primaryTemplate
          );
          primaryTemplateToSwitch = primaryTemplateToSwitch.length
            ? primaryTemplateToSwitch[0]
            : null;
        }
      }

      if (primaryTemplateToSwitch) {
        await executeTemplateSave(true, false);
        await activate(
          primaryTemplateToSwitch.id,
          "",
          primaryTemplateToSwitch.type ?? ""
        );
      } else {
        await executeTemplateSave(true);
      }
      updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));
      setRefreshTemplatesList(true);
    };

    const leaveWithoutSavingCommand = async () => {
      confirm.require({
        group: "leaveWithoutSaveConfirmDialog",
        message: t("templateBuilder.confirm.leaveWithoutSave.message"),
        header: t("templateBuilder.confirm.leaveWithoutSave.header"),
        icon: "far fa-exclamation-triangle",
        acceptLabel: t("yes"),
        acceptClass: "p-button-danger",
        rejectLabel: t("no"),
        rejectClass: "p-button-secondary",
        accept: async () => {
          store.commit("liveEditor/setIsExitCurrentChangesModalVisible", false);
          store.commit("liveEditor/setFormWidgetShowLabel", false);
          if (state.testEmailSent) {
            const rollbackPointIndex = getHistoryIndexFromGroupId(
              historyList.value,
              state.lastRollbackId
            );
            startHistoryRestore(1);
            try {
              await restoreToHistoryPoint(
                historyList.value,
                rollbackPointIndex,
                (current, total, actionDetail) => {
                  updateHistoryRestoreProgress(current, total, actionDetail);
                }
              );
              const rollbackSectionsOrder = historyList.value[rollbackPointIndex]?.sections_order_after;
              if (rollbackSectionsOrder) {
                refreshSectionsConfig(
                  JSON.parse(rollbackSectionsOrder).map((id: string) => id.toString())
                );
              }
              refreshTranslations();
              setTemplateConfigurationKeyInState(
                "currentIdTemplateElementsHistoryGroup",
                +state.lastRollbackId
              );
              await saveTemplate();
              setTestEmailSent(false);
            } finally {
              endHistoryRestore();
            }
          }

          // Check if opened from new flow system
          const flowEditingTemplate =
            store.getters["flowEditor/getEditingTemplate"];
          if (flowEditingTemplate !== null) {
            // New flow system - just deactivate and update store
            store.commit("flowEditor/setEditingTemplate", {
              ...flowEditingTemplate,
              status: FlowEditingTemplateStatusEnum.QUIT_TEMPLATE_EDITOR
            });
            deactivate(selectedTemplateId.value ?? 0);
          } else if (!openingFromCampaign.value) {
            state.templates.map(async s => {
              deactivate(selectedTemplateId.value ?? 0);
              if (state.hasAny) {
                await activate(
                  state.templates[state.index].id,
                  "",
                  state.templates[state.index].type
                );
                return true;
              }
              return false;
            });
          } else {
            const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
            const idCampaign = editingTemplateFromCampaign?.id_campaign ?? 0;
            if (idCampaign > 0) {
              state.templatesLinkedToCampaign
                .filter((c: any) => c.idCampaign === idCampaign)
                .map(async s => {
                  deactivate(selectedTemplateId.value ?? 0);
                  if (state.hasAny) {
                    await activate(
                      state.templates[state.index].id,
                      "",
                      state.templates[state.index].type
                    );
                    return true;
                  }
                  return false;
                });
            }
          }
        }
      });
    };

    const btnSaveComputed: ComputedRef<PrimvueMenuModel> = computed(() => {
      const button = {
        label: t("templateBuilder.save"),
        class: "p-button-secondary",
        items: [
          {
            label: t("templateBuilder.saveAndLeave"),
            command: saveAndLeaveCommand
          },
          {
            label: t("templateBuilder.saveAndStay"),
            command: async () => {
              const editingTemplateFromCampaign = getEditingTemplateFromCampaign();

              if (openingFromCampaign.value && editingTemplateFromCampaign) {
                const templatesLinkedToThisCampaign = state.templatesLinkedToCampaign.filter(
                  (c: any) =>
                    c.idCampaign === editingTemplateFromCampaign.id_campaign
                );
                const idTemplatesLinkedToThisCampaign = templatesLinkedToThisCampaign.map(
                  (c: any) => c.id
                );
                const primaryTemplate = editingTemplateFromCampaign.idTemplate;

                // on est dans un template de redirection
                if (
                  idTemplatesLinkedToThisCampaign.includes(
                    state.template?.id
                  ) &&
                  state.template?.id !== primaryTemplate
                ) {
                  await executeTemplateSave(false, false);
                  return true;
                }
              }

              await executeTemplateSave(false);
              updateLastRollbackGroupId(
                getLastPointOfSaveId(historyList.value)
              );

              // Update flowEditor store for new flow system (Save & Stay)
              const flowEditingTemplate =
                store.getters["flowEditor/getEditingTemplate"];
              if (
                flowEditingTemplate !== null &&
                flowEditingTemplate.status ===
                  FlowEditingTemplateStatusEnum.IN_TEMPLATE_EDITOR
              ) {
                store.commit("flowEditor/setEditingTemplate", {
                  ...flowEditingTemplate,
                  status:
                    FlowEditingTemplateStatusEnum.SAVE_STAY_TEMPLATE_EDITOR,
                  informations: state.template?.informations
                    ? {
                        subject: state.template.informations.subject,
                        fromName: state.template.informations.fromName,
                        fromEmail: state.template.informations.fromEmail,
                        previewText: state.template.informations.previewText,
                      }
                    : undefined,
                });
              }

              return true;
            }
          },
          {
            label: t("templateBuilder.leaveWithoutSaving"),
            command: leaveWithoutSavingCommand
          }
        ]
      };

      if (!openingFromCampaign.value) {
        button.items.splice(2, 0, {
          label: t("templateBuilder.minimize"),
          command: async () => hideTemplateEditor()
        });
      }

      if (selectedSyncElement.value) {
        button.items = [
          {
            label: t(
              "templateBuilder.editSyncElementActions.saveForAllInstances"
            ),
            command: commandSaveForAllInstances
          },
          {
            label: t("templateBuilder.editSyncElementActions.unlinkAndSave"),
            command: showUnlinkAndSaveModal
          },
          {
            label: t("templateBuilder.editSyncElementActions.previewInstances"),
            command: async () => {
              displaySyncElementTemplatesModal.value = true;
            }
          },
          {
            label: t("templateBuilder.leaveWithoutSaving"),
            command: commandCancelModifications
          },
          {
            label: t("templateBuilder.editSyncElementActions.help"),
            command: async () => {
              openHelp("164", false);
            }
          }
        ];
      }

      return button;
    });

    const toolbarRightButtons: ComputedRef<PrimvueMenuModel[]> = computed(
      () => [btnSaveComputed.value]
    );

    const advancedCssItems = computed(() => {
      const advancedCssMediaQueries = {
        label: t("templateBuilder.toolbar.openCssPanelMediaQueries"),
        class: state.leftToolbar.show.cssPanelMediaQueries
          ? "current-active-menu"
          : "",
        command: async () => {
          await togglePanel("cssPanelMediaQueries");
        }
      };

      const cssItems = [
        {
          label: t("templateBuilder.toolbar.openCssPanel"),
          class:
            state.leftToolbar.show.cssPanel &&
            !state.leftToolbar.show.cssPanelMediaQueries
              ? "current-active-menu"
              : "",
          command: async () => {
            await togglePanel("cssPanel");
          }
        }
      ];

      if (state.template) {
        const design = state.template.sections.find(
          section =>
            section.id_template_elements.toString() ===
            state.template?.content.design.toString()
        );
        if (design) {
          const designData = JSON.parse(design.data || "{}");
          if (!designData[HIDE_MOBILE_ATTRIBUTE]) {
            cssItems.push(advancedCssMediaQueries);
          }
        } else {
          cssItems.push(advancedCssMediaQueries);
        }
      } else {
        cssItems.push(advancedCssMediaQueries);
      }

      return cssItems;
    });

    const contentHeaderButtonsLeft = computed(() => [
      {
        multipleType: "buttons",
        items: [
          {
            icon: () =>
              state.template?.type === TemplateTypeEnum.TEASER
                ? "far fa-fw fa-sliders-h"
                : "far fa-fw fa-info",
            class: () =>
              `${state.leftToolbar.show.informationPanel && "active"}`,
            tooltip: () =>
              state.template?.type === TemplateTypeEnum.TEASER
                ? t("templateBuilder.teaser.displaySettings")
                : t("templateBuilder.toolbar.tooltip.information"),
            command: async () => {
              togglePanel("informationPanel", null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch(
                    "liveEditor/prepareStructureConfiguration",
                    selectedSyncElement.value.structureConfiguration
                  );
                }
              });
            }
          },
          {
            icon: "far fa-fw fa-cogs",
            class: () =>
              `${state.leftToolbar.show.configurationPanel && "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.configPanel"),
            hide:
              (!state.leftToolbar.show.configurationPanel ||
                isSmsTemplate(selectedTemplateType.value) ||
                isPushTemplate(selectedTemplateType.value) ||
                isRichTextingTemplate(selectedTemplateType.value) ||
                isWhatsappTemplate(selectedTemplateType.value)) &&
              (!selectedSyncElement.value ||
                (selectedSyncElement.value &&
                  !isPanelVisible(
                    selectedSyncElement.value.elementType,
                    "configurationPanel",
                    selectedSyncElement.value.elementPartType
                  ))),
            command: () => {
              togglePanel("configurationPanel", null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch(
                    "liveEditor/prepareStructureConfiguration",
                    selectedSyncElement.value.structureConfiguration
                  );
                } else {
                  removeActiveItemElementDecorations();
                  store.commit("liveEditor/resetSelectedStructure");
                }
              });
            }
          },
          {
            icon: "far fa-fw fa-th-large",
            class: () => `${state.leftToolbar.show.widgetsPanel && "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.insertWidget"),
            command: async () => {
              togglePanel("widgetsPanel", null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch(
                    "liveEditor/prepareStructureConfiguration",
                    selectedSyncElement.value.structureConfiguration
                  );
                }
              });
            },
            hide:
              isSmsTemplate(selectedTemplateType.value) ||
              isPushTemplate(selectedTemplateType.value) ||
              isRichTextingTemplate(selectedTemplateType.value) ||
              isWhatsappTemplate(selectedTemplateType.value) ||
              state.template?.informations.imported ||
              (selectedSyncElement.value &&
                !isPanelVisible(
                  selectedSyncElement.value.elementType,
                  "widgetsPanel",
                  selectedSyncElement.value.elementPartType
                ))
          },
          {
            icon: "far fa-fw fa-pager",
            class: () =>
              `${state.leftToolbar.show.loadElementsPanel && "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.loadElements"),
            command: () => {
              togglePanel("loadElementsPanel", null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch(
                    "liveEditor/prepareStructureConfiguration",
                    selectedSyncElement.value.structureConfiguration
                  );
                }
              });
            },
            hide:
              (!isEmailTemplate(selectedTemplateType.value) &&
                !isDisplayTemplate(selectedTemplateType.value)) ||
              state.template?.informations.imported ||
              (selectedSyncElement.value !== null &&
                !isPanelVisible(
                  selectedSyncElement.value.elementType,
                  "loadElementsPanel",
                  selectedSyncElement.value.elementPartType
                ))
          },
          {
            icon:
              isSmsTemplate(selectedTemplateType.value) ||
              isPushTemplate(selectedTemplateType.value) ||
              isRichTextingTemplate(selectedTemplateType.value)
                ? "far fa-fw fa-th-large"
                : "far fa-fw fa-paint-brush",
            tooltip:
              isSmsTemplate(selectedTemplateType.value) ||
              isPushTemplate(selectedTemplateType.value)
                ? t("templateBuilder.toolbar.tooltip.configPanel")
                : t("templateBuilder.toolbar.tooltip.designPanel"),
            class: () => `${state.leftToolbar.show.designPanel && "active"}`,
            command: async () => {
              // If template is not SMS or Push, or if another panel is opened, we let the user click the button
              if (
                (!isSmsTemplate(selectedTemplateType.value) &&
                  !isPushTemplate(selectedTemplateType.value)) ||
                !isRichTextingTemplate(selectedTemplateType.value) ||
                !isWhatsappTemplate(selectedTemplateType.value) ||
                !state.leftToolbar.show.designPanel
              ) {
                if (isEmailTemplate(selectedTemplateType.value)) {
                  getPageConfig();
                }

                await togglePanel("designPanel");
              }
            },
            hide:
              isFacebookTemplate(selectedTemplateType.value) ||
              state.template?.informations.imported ||
              (selectedSyncElement.value !== null &&
                !isPanelVisible(
                  selectedSyncElement.value.elementType,
                  "designPanel",
                  selectedSyncElement.value.elementPartType
                ))
          },
          {
            icon: "far fa-fw fa-text",
            class: (): string =>
              `${state.leftToolbar.show.translationPanel && "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.translationPanel"),
            command: async () => {
              togglePanel("translationPanel", null).then(() => {
                if (selectedSyncElement.value) {
                  store.dispatch(
                    "liveEditor/prepareStructureConfiguration",
                    selectedSyncElement.value.structureConfiguration
                  );
                }
              });
            },
            hide:
              selectedSyncElement.value !== null &&
              !isPanelVisible(
                selectedSyncElement.value.elementType,
                "translationPanel",
                selectedSyncElement.value.elementPartType
              )
          },
          {
            icon: "fab fa-fw fa-css3",
            class: () =>
              `${(state.leftToolbar.show.cssPanel ||
                state.leftToolbar.show.cssPanelMediaQueries) &&
                "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.advancedCss"),
            hide:
              !state.template?.informations.imported &&
              ((!isEmailTemplate(selectedTemplateType.value) &&
                !isDisplayTemplate(selectedTemplateType.value)) ||
                !advancedModeEnabled.value),
            items: advancedCssItems.value
          }
        ]
      },
      {
        multipleType: "buttons",
        hide:
          historyList.value.length === 0 || selectedSyncElement.value !== null,
        items: [
          {
            icon: "far fa-regular fa-clock",
            class: () =>
              `${(state.leftToolbar.show.historyPanel && "active") ||
                (historyList.value.length === 0 && "disabled")}`,
            tooltip: t("templateBuilder.toolbar.tooltip.historyPanel"),
            command: async () => {
              if (historyList.value.length !== 0) {
                const result = await getTemplateHistoryGroupListPaginated(
                  0,
                  HISTORY_PAGE_SIZE
                );
                historyList.value = result.items;
                historyHasMore.value = result.hasMore;
                historyTotalItems.value = result.total;
                await togglePanel("historyPanel");
              }
            }
          },
          {
            icon: "far fa-fw fa-undo",
            class: () =>
              `${firstHistoryGroupId.value === currentHistoryGroupId.value &&
                "disabled"}`,
            tooltip: `${t("templateBuilder.toolbar.tooltip.undo")} (Ctrl+Z)`,
            disabled: () =>
              firstHistoryGroupId.value === currentHistoryGroupId.value,
            command: () => {
              if (firstHistoryGroupId.value !== currentHistoryGroupId.value) {
                handlePreviousHistory();
              }
            }
          },
          {
            icon: "far fa-fw fa-redo",
            class: () =>
              `${lastHistoryGroupId.value === currentHistoryGroupId.value &&
                "disabled"}`,
            tooltip: `${t("templateBuilder.toolbar.tooltip.redo")} (Ctrl+Y)`,
            disabled: () =>
              lastHistoryGroupId.value === currentHistoryGroupId.value,
            command: () => {
              if (lastHistoryGroupId.value !== currentHistoryGroupId.value) {
                handleNextHistory();
              }
            }
          }
        ]
      },
      {
        multipleType: "buttons",
        hide: selectedSyncElement.value !== null,
        items: [
          {
            icon: "far fa-fw fa-paper-plane",
            hide:
              isDisplayTemplate(selectedTemplateType.value) ||
              isFacebookTemplate(selectedTemplateType.value),
            disabled: () => {
              if (isWhatsappTemplate(selectedTemplateType.value)) {
                const status = whatsappCombinedStatus.value;
                if (!status) return true;
                const sendable = [WhatsappValidationStatus.APPROVED, WhatsappValidationStatus.REINSTATED];
                return !sendable.includes(status as WhatsappValidationStatus);
              }
              return false;
            },
            tooltip: t("templateBuilder.toolbar.tooltip.sendTestMessage"),
            command: async () => {
              if (!isWhatsappTemplate(selectedTemplateType.value)) {
                // Save template before sending a test message
                await executeTemplateSave(
                  false,
                  false,
                  false,
                  HistoryType.INTERMEDIATE_POINT_OF_SAVE
                );
                // say that the test email has been sent
                setTestEmailSent(true);

                updateLastRollbackGroupId(
                  getLastPointOfSaveId(historyList.value)
                );

                if (isPushTemplate(selectedTemplateType.value)) {
                  // If push notification template, we ask for browser authorization and we sent the notification
                  sendTestNotification(getTestPushNotificationData());
                } else if (isRichTextingTemplate(selectedTemplateType.value)) {
                  store.commit("general/showSendTestEmailV4Modal", state.template?.id);
                } else {
                  // Validate InformationPanel values
                  const validation = await informationPanelSettingsValidate();
                  informationPanelValidation.value = validation.validate;

                  if (!validation.success) {
                    // Open InformationPanel to see errors
                    if (
                      !state.isLeftToolbarVisible ||
                      state.leftToolbar.show.informationPanel === false
                    ) {
                      await togglePanel("informationPanel");
                    }
                  } else {
                    const isEmailV4Template = (
                      isEmailTemplate(selectedTemplateType.value) && state.template?.version === 4
                    );
                    const shouldOpenUnifiedModal = isEmailV4Template
                      || isSmsTemplate(selectedTemplateType.value);
                    if (shouldOpenUnifiedModal) {
                      store.commit("general/showSendTestEmailV4Modal", state.template?.id);
                    } else {
                      // If email or SMS, we open the modal
                      store.commit("general/showSendTestModal");
                    }
                  }
                }
              } else {
                confirm.require({
                  group: "sendTestWhatsapp",
                  message: t(
                    "templateBuilder.confirm.sendTestWhatsapp.message"
                  ),
                  header: t("templateBuilder.confirm.sendTestWhatsapp.header"),
                  icon: "far fa-exclamation-triangle",
                  acceptLabel: t("yes"),
                  rejectLabel: t("no"),
                  accept: async () => {
                    // Validate InformationPanel values
                    const validation = await informationPanelSettingsValidate();
                    informationPanelValidation.value = validation.validate;

                    if (!validation.success) {
                      // Open InformationPanel to see errors
                      if (
                        !state.isLeftToolbarVisible ||
                        state.leftToolbar.show.informationPanel === false
                      ) {
                        await togglePanel("informationPanel");
                      }
                    } else {
                      store.commit("general/showSendTestEmailV4Modal", state.template?.id);
                    }
                  },
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  reject: () => {}
                });
              }
            }
          },
          {
            icon: "far fa-cog",
            tooltip: t("templateBuilder.toolbar.tooltip.advancedCss"),
            hide:
              isSmsTemplate(selectedTemplateType.value) ||
              isPushTemplate(selectedTemplateType.value) ||
              isRichTextingTemplate(selectedTemplateType.value) ||
              isWhatsappTemplate(selectedTemplateType.value) ||
              isFacebookTemplate(selectedTemplateType.value) ||
              state.template?.informations.imported,
            items: [
              {
                label: t("templateBuilder.toolbar.toggleAdvancedMode"),
                icon: !state.advancedModeEnabled
                  ? "far fa-square"
                  : "far fa-check-square",
                iconPos: "left",
                command: () => {
                  toggleAdvancedMode();
                }
              }
            ]
          }
        ]
      },
      {
        multipleType: "buttons",
        hide:
          isSmsTemplate(selectedTemplateType.value) ||
          isPushTemplate(selectedTemplateType.value) ||
          isRichTextingTemplate(selectedTemplateType.value) ||
          isWhatsappTemplate(selectedTemplateType.value) ||
          isFacebookTemplate(selectedTemplateType.value),
        items: [
          {
            icon: "far fa-fw fa-desktop",
            class: () => `${width.value === TemplateWidth.DESKTOP && "active"}`,
            tooltip: t("templateBuilder.toolbar.tooltip.desktopView"),
            command: () => {
              setEditorViewport("desktop");
            }
          },
          {
            icon: "far fa-fw fa-mobile",
            class: () => `${isMobileViewport() && "active"}`,
            tooltip: () => {
              if (getTemplateIframeDocument()?.body?.getAttribute(HIDE_MOBILE_ATTRIBUTE) === 'true') {
                return t('templateBuilder.fields.warningMessages.disabledMobileDesign');
              }
              return `${t("templateBuilder.toolbar.tooltip.mobileView")} — ${t(state.mobileDevice.labelKey)} · ${state.mobileDevice.width} px`;
            },
            disabled: () => getTemplateIframeDocument()?.body?.getAttribute(HIDE_MOBILE_ATTRIBUTE) === 'true',
            command: () => {
              setEditorViewport(state.mobileDevice);
            }
          },
          {
            label: `${state.mobileDevice.width} px`,
            tooltip: t("templateBuilder.toolbar.tooltip.mobileDevice"),
            hide: !isMobileViewport(),
            items: EDITOR_MOBILE_DEVICES.map(device => ({
              label: `${t(device.labelKey)} · ${device.width} px`,
              icon:
                device.key === state.mobileDevice.key
                  ? "far fa-check-circle"
                  : "far fa-circle",
              command: () => {
                setEditorViewport(device);
              }
            }))
          }
        ]
      }
    ]);

    const closeDuplicationModal = () => {
      showDuplicationModal.value = false;
    };

    const { duplicateTemplate } = useTemplatesApi();

    const duplicateConfirmationDialog = (newName: string) => {
      showDuplicationModal.value = false;
      duplicateTemplate(selectedTemplateId.value, { name: newName }).then(
        result => {
          if (result.success && result.data?.id_template) {
            showToastSuccess(t("templates.confirm.duplicate.success.message"));
            updateSectionsInState(false);
            activate(result.data.id_template, "", selectedTemplateType.value);
            setRefreshTemplatesList(true);
          } else {
            showToastError(t("templates.confirm.duplicate.error.message"));
          }
        }
      );
    };

    const saveToMyTemplatesConfirm = (newName: string) => {
      showSaveToMyTemplatesModal.value = false;
      duplicateTemplate(selectedTemplateId.value, { name: newName }).then(
        result => {
          if (result.success && result.data?.id_template) {
            showToastSuccess(t("templateBuilder.saveToMyTemplatesModal.success"));
          } else {
            showToastError(t("templateBuilder.saveToMyTemplatesModal.error"));
          }
        }
      );
    };

    const closeSaveToMyTemplatesModal = () => {
      showSaveToMyTemplatesModal.value = false;
    };

    const changeTab = (s: any) => {
      restoreTemplateBeforeEdit();
      updateSectionsInState(false);
      store.commit("liveEditor/setFormWidgetShowLabel", false);
      activate(s.id, s.informations.name, s.type);
    };

    const tabs: ComputedRef<PrimvueMenuModel[]> = computed(() => {
      if (selectedSyncElement.value) {
        return [];
      }
      let buttons: PrimvueMenuModel[] = [];
      if (!openingFromCampaign.value) {
        buttons = state.templates.map(s => {
          let isActiveTemplate = s.id === selectedTemplateId.value;
          if (
            state.template &&
            state.template.originTemplateId &&
            String(state.template.originTemplateId) === String(s.id)
          ) {
            isActiveTemplate = true;
          }

          const fullName = s.informations.name;
          const truncated = truncateLabel(fullName, 25);
          return {
            label: truncated,
            tooltip: truncated !== fullName ? fullName : undefined,
            class: `${
              isActiveTemplate ? "p-button-primary" : "p-button-secondary"
            } footer-btn`,
            command: !isActiveTemplate
              ? () => {
                  changeTab(s);
                }
              : undefined,
            items: [
              {
                label: t("templateBuilder.rename"),
                command: () => {
                  valueRenameTemplateModal.value = s.informations.name;
                  displayRenameTemplateModal.value = true;
                }
              },
              {
                label: t("templateBuilder.duplicate"),
                command: () => {
                  newListName.value =
                    t("myLists.manage.actions.cloneText") + s.informations.name;
                  showDuplicationModal.value = true;
                }
              },
              {
                label: t("templateBuilder.close"),
                command: () => {
                  if (state.template?.edited) {
                    store.commit(
                      "liveEditor/setIsExitCurrentChangesModalVisible",
                      true
                    );
                  } else {
                    deactivate(selectedTemplateId.value ?? 0);
                    if (state.hasAny) {
                      activate(
                        state.templates[state.index].id,
                        "",
                        state.templates[state.index].type
                      );
                    }
                  }
                }
              }
            ]
          };
        });
        buttons.push({
          icon: "far fa-plus",
          class: "create-new-btn p-button-primary footer-btn",
          command: () => {
            showTemplateSelector.value = true;
          }
        });
      } else {
        const editingTemplateFromCampaign = getEditingTemplateFromCampaign();
        const idCampaign = editingTemplateFromCampaign?.id_campaign ?? 0;
        if (idCampaign > 0) {
          buttons = state.templatesLinkedToCampaign
            .filter((c: any) => c.idCampaign === idCampaign)
            .map(s => {
              const isActiveTemplate = s.id === selectedTemplateId.value;

              const fullName = s.informations.name;
              const truncated = truncateLabel(fullName, 25);
              return {
                label: truncated,
                tooltip: truncated !== fullName ? fullName : undefined,
                class: `${
                  isActiveTemplate ? "p-button-primary" : "p-button-secondary"
                } footer-btn`,
                command: !isActiveTemplate
                  ? () => {
                      updateSectionsInState(false);
                      activate(s.id, s.informations.name, s.type);
                    }
                  : undefined,
                items: [
                  {
                    label: t("templateBuilder.rename"),
                    command: () => {
                      valueRenameTemplateModal.value = s.informations.name;
                      displayRenameTemplateModal.value = true;
                    }
                  },
                  {
                    label: t("templateBuilder.duplicate"),
                    command: () => {
                      newListName.value =
                        t("myLists.manage.actions.cloneText") +
                        s.informations.name;
                      showDuplicationModal.value = true;
                    }
                  },
                  {
                    label: t("templateBuilder.close"),
                    command: () => {
                      if (state.template?.edited) {
                        store.commit(
                          "liveEditor/setIsExitCurrentChangesModalVisible",
                          true
                        );
                      } else {
                        deactivate(selectedTemplateId.value ?? 0);
                        if (state.hasAny) {
                          activate(
                            state.templates[state.index].id,
                            "",
                            state.templates[state.index].type
                          );
                        }
                      }
                    }
                  }
                ]
              };
            });
        }
      }
      return buttons;
    });

    // Needed for embed template preview udpate selector
    const lastUpdateSelectorVersion = ref();
    const displayPreviewWebSocket = ref<WebSocket | null>();
    const updateDisplayPreview = computed(
      () => store.getters["liveEditor/getUpdateDisplayPreview"]
    );

    // Preview URL modal
    const showPreviewUrlModal = ref(false);
    const previewUrlPath = ref("");
    const previewUrlError = ref("");
    const previewUrlLoading = ref(false);
    const shopBaseUrl = computed(() => {
      const url = (isDemoModeActive.value && demoOverrides.url)
        ? demoOverrides.url
        : (UserState.activeShop?.url || "");
      return url.replace(/\/+$/, "");
    });

    const getPreviewUrlStorageKey = () =>
      `spm-preview-path:${idShop}:${selectedTemplateId.value}`;

    const openPreviewUrlModal = () => {
      const stored = localStorage.getItem(getPreviewUrlStorageKey());
      previewUrlPath.value = stored || "";
      previewUrlError.value = "";
      previewUrlLoading.value = false;
      showPreviewUrlModal.value = true;
    };

    const confirmPreviewUrl = async () => {
      previewUrlError.value = "";
      previewUrlLoading.value = true;

      const path = previewUrlPath.value.trim();
      const fullUrl = shopBaseUrl.value + (path.startsWith("/") ? path : path ? "/" + path : "");

      // Close previous preview window
      if ((window as any).__spmPreviewWindow && !(window as any).__spmPreviewWindow.closed) {
        (window as any).__spmPreviewWindow.close();
      }
      if (displayPreviewWebSocket.value) {
        displayPreviewWebSocket.value.onclose = null;
        displayPreviewWebSocket.value.onerror = null;
        if (displayPreviewWebSocket.value.readyState === WebSocket.OPEN ||
            displayPreviewWebSocket.value.readyState === WebSocket.CONNECTING) {
          displayPreviewWebSocket.value.close();
        }
        displayPreviewWebSocket.value = null;
      }

      // Open a new window (unique name to avoid reuse)
      const windowWidth = window.innerWidth * 0.95;
      const height = (windowWidth * window.innerHeight) / window.innerWidth;
      const previewWindow = window.open(
        "about:blank",
        "previewDisplayWindow_" + Date.now(),
        `width=${windowWidth}, height=${height}, top=${(window.innerHeight - height) / 2}, left=${(window.innerWidth - windowWidth) / 2}`
      );
      (window as any).__spmPreviewWindow = previewWindow;

      try {
        // Save path to localStorage
        if (path) {
          localStorage.setItem(getPreviewUrlStorageKey(), path);
        } else {
          localStorage.removeItem(getPreviewUrlStorageKey());
        }

        // In demo mode, skip script check (prospect site has no ShopImind tag — extension handles injection)
        if (!isDemoModeActive.value) {
          // Check if preview script is present on the target page
          const checkResult = await nestCall(
            "post",
            NestApiType.DEFAULT,
            "/template/check-preview-script",
            {},
            "",
            { idShop, url: fullUrl }
          );

          if (!checkResult || checkResult.error) {
            previewUrlError.value = t("templateBuilder.previewUrlModal.errorUnexpected");
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }

          if (!checkResult.available) {
            previewUrlError.value =
              checkResult.error === "fetch_failed"
                ? t("templateBuilder.previewUrlModal.errorFetchFailed")
                : t("templateBuilder.previewUrlModal.errorScriptNotFound");
            previewUrlLoading.value = false;
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            return;
          }
        }

        // Proceed with preview
        const currentTemplateVersion = getCurrentTemplateVersion(idShop);
        if (currentTemplateVersion) {
          const { data, params } = currentTemplateVersion;
          try {
            const response = await nestCall(
              "post",
              NestApiType.DEFAULT,
              "/template/save-preview-display",
              {},
              "",
              {
                ...params,
                ...data
              }
            );

            const token = response?.token;
            if (!token) {
              previewUrlError.value = t("templateBuilder.previewUrlModal.errorUnexpected");
              previewUrlLoading.value = false;
              if (previewWindow && !previewWindow.closed) previewWindow.close();
              return;
            }

            const previewFullUrl = new URL(fullUrl);
            previewFullUrl.searchParams.append("spm-preview", token);

            // Navigate the already-opened window to the preview URL
            if (previewWindow && !previewWindow.closed) {
              previewWindow.location.href = previewFullUrl.toString();
            }

            showPreviewUrlModal.value = false;
          } catch (error) {
            console.error("[Preview] Error saving preview display:", error);
            previewUrlError.value = t("templateBuilder.previewUrlModal.errorUnexpected");
            if (previewWindow && !previewWindow.closed) previewWindow.close();
          }
        }
      } catch (error) {
        console.error("[Preview] Error checking preview script:", error);
        previewUrlError.value = t("templateBuilder.previewUrlModal.errorUnexpected");
        if (previewWindow && !previewWindow.closed) previewWindow.close();
      }

      previewUrlLoading.value = false;
    };

    const submitWhatsappTemplate = async () => {
      if (isSubmittingWhatsapp.value) return;
      isSubmittingWhatsapp.value = true;

      try {
        // 1. Save the template first
        await executeTemplateSave(false, false, true);

        // 2. Check if WhatsApp Business service is configured
        const notConfigured = await getNotConfiguredServices(idShop, ["boxsendwhatsapp"]);

        if (notConfigured.length > 0) {
          // Show service configuration modal, retry submit on success
          store.commit("general/setNotConfiguredServices", notConfigured);
          store.commit("general/setIsServicesParametersModalVisible", true);
          store.commit("general/setServiceParameterSuccessAction", () => {
            isSubmittingWhatsapp.value = false;
            submitWhatsappTemplate();
          });
          isSubmittingWhatsapp.value = false;
          return;
        }

        // 3. Submit root template to Meta via backend (originTemplateId is the root, even when on a funnel)
        const rootTemplateId = state.template?.originTemplateId
          ? parseInt(state.template.originTemplateId, 10)
          : selectedTemplateId.value;
        const response = await SubmitWhatsappValidation(idShop, {
          template_display_selector_id: rootTemplateId,
        });

        if (response.success && response.data?.template) {
          const { status } = response.data.template;

          // 4. Update root + funnel statuses and category in state
          updateWhatsappValidationStatuses(status, response.data.template, response.data.funnels);

          // 5. Refresh initial hash so future edits are detected as changes
          setInitialWhatsappDataHash(openingFromCampaign.value);

          // 6. Show toast with per-status message
          const statusKey = status as string;
          const translationKey = `templateBuilder.submitWhatsappStatus.${statusKey}`;
          const message = t(translationKey);
          const sendableStatuses = [
            WhatsappValidationStatus.APPROVED,
            WhatsappValidationStatus.REINSTATED,
            WhatsappValidationStatus.PENDING,
            WhatsappValidationStatus.IN_APPEAL,
          ];
          if (sendableStatuses.includes(statusKey as WhatsappValidationStatus)) {
            showToastSuccess(message);
          } else {
            showToastError(message);
          }
        } else {
          showToastError(t("templateBuilder.submitWhatsappStatus.ERROR"));
        }
      } catch (error) {
        console.error("[WhatsApp Submit] Failed:", error);
        showToastError(t("templateBuilder.submitWhatsappStatus.ERROR"));
      } finally {
        isSubmittingWhatsapp.value = false;
      }
    };

    // WhatsApp combined status display helpers
    const whatsappCombinedStatus = computed((): string | null => {
      if (!isWhatsappTemplate(selectedTemplateType.value)) return null;
      const originId = state.template?.originTemplateId || String(selectedTemplateId.value);
      const rootTmpl = state.templates.find(
        (tmpl) => String(tmpl.id) === String(originId)
      ) || state.templatesLinkedToCampaign.find(
        (tmpl) => String(tmpl.id) === String(originId)
      );
      const rootStatus = rootTmpl?.configuration?.templateValidationStatus;
      if (!rootStatus || rootStatus === WhatsappValidationStatus.NOT_SUBMITTED) return null;

      const allStatuses: (string | null)[] = [rootStatus];
      const funnelTemplates = state.templatesFromFunnel[originId] || [];
      for (const funnelTmpl of funnelTemplates) {
        const fStatus = funnelTmpl.configuration?.templateValidationStatus || null;
        allStatuses.push(fStatus);
      }

      // If any template has no status (not yet submitted), return null
      const hasUnsubmitted = allStatuses.some((s) => !s || s === WhatsappValidationStatus.NOT_SUBMITTED);
      if (hasUnsubmitted) return null;

      // Combined status logic: blocking > pending > sendable
      const sendable = [WhatsappValidationStatus.APPROVED, WhatsappValidationStatus.REINSTATED];
      const pending = [WhatsappValidationStatus.PENDING, WhatsappValidationStatus.IN_APPEAL];
      const isSendable = (s: string | null) => !!s && sendable.includes(s as WhatsappValidationStatus);
      const isPending = (s: string | null) => !!s && pending.includes(s as WhatsappValidationStatus);
      const isBlocking = (s: string | null) => !!s && !isSendable(s) && !isPending(s);

      const blocking = allStatuses.find(isBlocking);
      if (blocking) return blocking;
      const pendingFound = allStatuses.find(isPending);
      if (pendingFound) return pendingFound;
      if (allStatuses.every(isSendable)) return WhatsappValidationStatus.APPROVED;
      return allStatuses[0] || null;
    });

    const getWhatsappStatusIcon = (status: string): string => {
      const iconMap: Record<string, string> = {
        APPROVED: 'fa-solid fa-circle-check',
        REINSTATED: 'fa-solid fa-circle-check',
        PENDING: 'fa-solid fa-clock',
        IN_APPEAL: 'fa-solid fa-clock',
        REJECTED: 'fa-solid fa-circle-xmark',
        PAUSED: 'fa-solid fa-circle-pause',
        DISABLED: 'fa-solid fa-circle-minus',
        FLAGGED: 'fa-solid fa-flag',
        ARCHIVED: 'fa-solid fa-box-archive',
        DELETED: 'fa-solid fa-trash',
        PENDING_DELETION: 'fa-solid fa-trash',
        LIMIT_EXCEEDED: 'fa-solid fa-circle-exclamation',
        LOCKED: 'fa-solid fa-lock',
        ERROR: 'fa-solid fa-circle-exclamation',
      };
      return iconMap[status] || 'fa-solid fa-circle-question';
    };

    const getWhatsappStatusColors = (status: string): { bg: string; color: string } => {
      const sendable = ['APPROVED', 'REINSTATED'];
      const pending = ['PENDING', 'IN_APPEAL'];
      if (sendable.includes(status)) return { bg: '#ecfdf5', color: '#059669' };
      if (pending.includes(status)) return { bg: '#fffbeb', color: '#d97706' };
      return { bg: '#fef2f2', color: '#dc2626' };
    };

    const getWhatsappStatusLabel = (status: string): string => {
      const key = `templates.whatsappStatus.${status}`;
      const translated = t(key);
      return translated !== key ? translated : status;
    };

    const whatsappStatusInfoMessage = computed((): string => {
      const status = whatsappCombinedStatus.value;
      if (!status) return '';
      const key = `templateBuilder.whatsappStatusInfo.${status}`;
      const translated = t(key);
      return translated !== key ? translated : '';
    });

    const contentHeaderEndToolbar = computed(() => [
      {
        icon: "fa-brands fa-whatsapp",
        label: t("templateBuilder.submitWhatsapp"),
        command: submitWhatsappTemplate,
        hide:
          !isWhatsappTemplate(selectedTemplateType.value) ||
          openingFromCampaign.value ||
          !!whatsappCombinedStatus.value
      },
      {
        icon: "far fa-eye",
        label: t("templateBuilder.preview"),
        command: () => {
          if (state.template?.version === 4) {
            store.commit("general/showPreviewDisplayV4Modal");
          } else {
            openPreviewUrlModal();
          }
        },
        hide: !isDisplayTemplate(selectedTemplateType.value)
      },
      {
        icon: "far fa-arrow-up-right-dots",
        label: t("templateBuilder.migrateToV4"),
        command: () => {
          store.commit("general/showMigrateV4Modal");
        },
        // Fonctionnalité pas assez testée : bouton masqué temporairement.
        // Pour le réactiver, restaurer la condition d'origine ci-dessous.
        hide: true
        // hide:
        //   state.template?.version !== 3 ||
        //   (!isEmailTemplate(selectedTemplateType.value) &&
        //     !isDisplayTemplate(selectedTemplateType.value))
      },
      {
        icon: "far fa-bookmark",
        label: t("templateBuilder.saveToMyTemplates"),
        command: () => {
          saveToMyTemplatesName.value = state.template?.informations.name
            ? t("myLists.manage.actions.cloneText") + state.template.informations.name
            : "";
          showSaveToMyTemplatesModal.value = true;
        },
        hide:
          !openingFromCampaign.value ||
          (state.template?.version === 3 &&
            (isEmailTemplate(selectedTemplateType.value) ||
              isDisplayTemplate(selectedTemplateType.value)))
      }
    ]);

    const stopWatchers: any[] = [];

    stopWatchers.push(
      watch(
        () => selectedStructure.value,
        values => {
          if (
            values &&
            values.type !== TemplateStructureEnum.PAGE &&
            !isConfigurationPanelVisible.value
          ) {
            showConfigurationPanel();
          }

          if (
            values &&
            values.type !== TemplateStructureEnum.PAGE &&
            !isLeftToolbarVisible.value
          ) {
            togglePanel("configurationPanel");
          }
        }
      )
    );

    stopWatchers.push(
      watch(
        () => state.leftToolbar.show,
        () => {
          if (state.leftToolbar.show.designPanel) {
            // If we show designPanel, prepare structure configuration
            prepareStructureConfiguration();
          }
        },
        { deep: true }
      )
    );

    stopWatchers.push(
      watch(
        () => state.leftToolbar.show.translationPanel,
        async () => {
          if (!state.leftToolbar.show.translationPanel) {
            refreshAttributeInDOM(locale.value);

            // Restore original code of all smart lists
            restoreSmartListsOriginalCode();

            const defaultLanguage = getShopDefaultLang();
            updateIframeTranslations(
              state.template?.translations.filter(
                translation => translation.language === defaultLanguage
              ) ?? []
            );

            resetNeedToTranslate();

            // Store original code of lists
            refreshSmartProductsListsOriginalStructure();

            // Recalculate smart list with default language
            refreshSmartProductList();

            resetListeners();
          } else {
            // Restore original code of all smart lists
            restoreSmartListsOriginalCode();

            // Update sections in state first to sync DOM changes, then fix misplaced and remove unused translations
            updateSectionsInState(false);
            fixMisplacedTranslations();
            removeUnusedTranslations();

            fixMissingTranslations();
            sortTranslationInState();
            // Store original code of lists
            refreshSmartProductsListsOriginalStructure();

            // Recalculate smart list with default language
            refreshSmartProductList();
          }
        }
      )
    );

    stopWatchers.push(
      watch(lastHistoryGroupId, async newValue => {
        historyList.value = await getTemplateHistoryGroupList();
        setLastHistory(newValue);
      })
    );

    watch(
      () => syncElementWarning,
      () => {
        if (syncElementWarning.value) {
          showEditSyncElementWarning(
            confirm,
            t,
            syncElementWarning.value.elementType,
            syncElementWarning.value.acceptCallback,
            syncElementWarning.value.rejectCallback,
            syncElementWarning.value.additionalMessage ?? "",
            syncElementWarning.value.overrideMessage ?? "",
            syncElementWarning.value.elementPartType
          );
        }
      },
      { deep: true }
    );

    watch(updateDisplayPreview, () => {
      if (updateDisplayPreview.value) {
        const currentTemplateVersion = getCurrentTemplateVersion(idShop);
        if (currentTemplateVersion) {
          const { data, params } = currentTemplateVersion;
          const isV4 = state.template?.version === 4;
          const messageData: Record<string, any> = {
            ...params,
            ...data
          };
          if (isV4) {
            messageData.isV4 = true;
            try {
              const storageKey = `sendTestV4_${state.template?.id}`;
              const stored = getLocalStorageElement(storageKey);
              if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.contactId) {
                  messageData.contactId = parsed.contactId;
                }
                if (typeof parsed.previewLang === "string" && parsed.previewLang.trim() !== "") {
                  messageData.lang = parsed.previewLang;
                } else {
                  messageData.lang = getShopDefaultLang();
                }
                if (
                  parsed.conditionOverrides &&
                  typeof parsed.conditionOverrides === "object"
                ) {
                  messageData.conditionOverrides = parsed.conditionOverrides;
                  messageData.previewOptions = { annotateConditions: true };
                }
              } else {
                messageData.lang = getShopDefaultLang();
              }
            } catch {
              // Ignore parse errors
              messageData.lang = getShopDefaultLang();
            }
          }
          if (
            displayPreviewWebSocket.value &&
            displayPreviewWebSocket.value.readyState === WebSocket.OPEN
          ) {
            displayPreviewWebSocket.value.send(
              JSON.stringify({
                event: "preview-display-updated",
                data: messageData
              })
            );
          }
        }
        store.commit("liveEditor/setUpdateDisplayPreview", false);
      }
    });

    onBeforeMount(async () => {
      historyList.value = await getTemplateHistoryGroupList();

      updateLastRollbackGroupId(getLastPointOfSaveId(historyList.value));
      if (
        !state.template?.configuration.currentIdTemplateElementsHistoryGroup
      ) {
        // In case we open a template directly from database, we set the last point of save as the current history point
        const lastPointOfSave = getClosestPointOfSave(historyList.value);
        const lastHistoryPointOfSave: TemplatesElementsHistoryGroup =
          historyList.value[lastPointOfSave];

        if (lastHistoryPointOfSave) {
          setTemplateConfigurationKeyInState(
            "currentIdTemplateElementsHistoryGroup",
            lastHistoryPointOfSave.id_template_elements_history_group
          );
        }
      }
      setLastHistory(currentHistoryGroupId.value);

      if (historyList.value.length === 0 && state.template) {
        // Create first point of save of template if history list is empty
        // Mark template as changed to bypass the unsaved changes check in createHistory,
        // otherwise the initial POINT_OF_SAVE is silently skipped and the first edit cannot be undone
        markTemplateAsChanged(state.template.id);
        await createHistory(HistoryType.POINT_OF_SAVE);
      }
    });

    // Surveiller les conflits de version détectés
    watch(
      () => state.versionConflicts,
      async conflicts => {
        if (conflicts && conflicts.length > 0) {
          // Traiter chaque conflit détecté
          for (const conflict of conflicts) {
            await new Promise<void>(resolve => {
              // Stocker les données du conflit dans les refs
              currentConflictMessage.value = String(
                t("templateBuilder.confirm.versionConflict.message", {
                  templateName: conflict.templateName
                })
              );
              currentConflictLocalDate.value = conflict.localDate;
              currentConflictRemoteDate.value = conflict.remoteDate;

              confirm.require({
                group: "versionConflict",
                message: currentConflictMessage.value,
                header: t("templateBuilder.confirm.versionConflict.header"),
                icon: "far fa-exclamation-triangle",
                acceptLabel: t(
                  "templateBuilder.confirm.versionConflict.loadRecent"
                ),
                acceptClass: "p-button-primary",
                rejectLabel: t(
                  "templateBuilder.confirm.versionConflict.keepLocal"
                ),
                rejectClass: "p-button-secondary",
                accept: async () => {
                  // L'utilisateur choisit de charger depuis la BDD
                  setPreventDateModificationUpdate(false);
                  await reloadTemplateFromDatabase(conflict.templateId);
                  resolve();
                },
                reject: () => {
                  // L'utilisateur choisit de continuer avec ses modifications
                  setPreventDateModificationUpdate(false);
                  resolve();
                }
              });
            });
          }

          // Réinitialiser les conflits après traitement
          clearVersionConflicts();
          // Réinitialiser le flag de prévention (au cas où l'utilisateur aurait fermé le dialog sans choisir)
          setPreventDateModificationUpdate(false);
        }
      },
      { deep: true }
    );

    const handleKeyboardShortcuts = (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input, textarea or contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      // Don't intercept when iframe has focus (user editing template content)
      if (document.activeElement?.tagName === "IFRAME") {
        return;
      }

      if (e.ctrlKey && !e.shiftKey && !e.altKey) {
        if (e.key === "z") {
          e.preventDefault();
          if (firstHistoryGroupId.value !== currentHistoryGroupId.value) {
            handlePreviousHistory();
          }
        } else if (e.key === "y") {
          e.preventDefault();
          if (lastHistoryGroupId.value !== currentHistoryGroupId.value) {
            handleNextHistory();
          }
        }
      }
    };

    onMounted(() => {
      window.addEventListener("unload", saveTemplatesInLocalStorage);
      document.addEventListener("keydown", handleKeyboardShortcuts);
      // Initialiser le système de repositionnement automatique des icônes
      initIconPositioning();
      // Initialiser le système de labels DOM repositionnables
      initDOMLabels();
    });

    onBeforeUnmount(async () => {
      store.commit("liveEditor/setSelectedSyncElement", null);
      document.removeEventListener("keydown", handleKeyboardShortcuts);
      document.removeEventListener("dragover", trackMousePosition);
      saveTemplatesInLocalStorage();
      window.removeEventListener("unload", saveTemplatesInLocalStorage);
      destroyAllSortables();
      stopWatchers.forEach(stop => stop());

      // Memory cleanup: DOM labels observers & listeners
      cleanupDOMLabels();

      // Memory cleanup: Icon positioning observer
      cleanupIconPositioning();

      // Memory cleanup: WhatsApp carousel observers
      cleanupWhatsappObservers();

      // Memory cleanup: RCS carousel observers
      cleanupRcsObservers();

      // Memory cleanup: Countdown intervals
      cleanupAllCountdownIntervals();

      // Memory cleanup: Editor background bindings
      cleanupAllEditorBackgroundBindings();

      // Stop scrolling widgets listener
      stopScrolling();
      lastUpdateSelectorVersion.value = null;

      // Clean up preview resources only if a preview was active
      if ((window as any).__spmPreviewWindow || displayPreviewWebSocket.value) {
        try {
          await nestCall(
            "post",
            NestApiType.DEFAULT,
            "/template/preview-update-selector/",
            {},
            "",
            {
              idShop,
              idTemplate: selectedTemplateId.value,
              selector: "",
              position: "",
              toDelete: true
            }
          );
        } catch {
          // Ignore errors during cleanup
        }
      }
      if ((window as any).__spmPreviewWindow && !(window as any).__spmPreviewWindow.closed) {
        (window as any).__spmPreviewWindow.close();
      }
      (window as any).__spmPreviewWindow = null;
      if (displayPreviewWebSocket.value) {
        displayPreviewWebSocket.value.onclose = null;
        displayPreviewWebSocket.value.onerror = null;
        if (displayPreviewWebSocket.value.readyState === WebSocket.OPEN) {
          displayPreviewWebSocket.value.close();
        }
      }
      displayPreviewWebSocket.value = null;
      store.commit("liveEditor/setDisplayUrl", "");
    });

    const closeTemplateSelector = () => {
      showTemplateSelector.value = false;
    };

    const selectTemplateFromTemplateSelector = async (
      idTemplate: number,
      label: string,
      type: string
    ) => {
      activate(idTemplate, label, type).then(() => {
        showTemplateSelector.value = false;
        store.commit("general/hideTheSpinner");
      });
    };

    const setDisplayUrl = () => {
      const template = getTemplateIframeDocument();
      if (template) {
        const body = template.querySelector("#spm_body");
        const targetUrl = body?.getAttribute("data-target-url");
        store.commit(
          "liveEditor/setDisplayUrl",
          (targetUrl ? atob(targetUrl) : "") ||
            (UserState.activeShop?.url ?? "")
        );
      }
    };

    /**
     * Execute functions after iframe is loaded
     */
    const iframeLoaded = async (done: Function) => {
      try {
      // Initialize WhatsApp hash tracking when opening from scenario
      if (isWhatsappTemplate(selectedTemplateType.value)) {
        setInitialWhatsappDataHash(openingFromCampaign.value);
      }

      // Skip panel switching if we're restoring history (preserve current panel)
      if (!isRestoringHistory.value) {
        if (
          isSmsTemplate(selectedTemplateType.value) ||
          isPushTemplate(selectedTemplateType.value) ||
          isRichTextingTemplate(selectedTemplateType.value) ||
          isWhatsappTemplate(selectedTemplateType.value)
        ) {
          await togglePanel("designPanel");
          prepareStructureConfiguration();
        } else if (state.template?.informations.imported) {
          await togglePanel("translationPanel");
        } else if (
          isEmailTemplate(selectedTemplateType.value) &&
          !state.template?.informations.imported
        ) {
          await togglePanel("widgetsPanel");
        } else if (isDisplayTemplate(selectedTemplateType.value)) {
          // Set display url
          setDisplayUrl();
          // Websocket to check if selector has been updated in the preview
          if (displayPreviewWebSocket.value) {
            // Neutralize handlers to prevent reconnection from old closure
            displayPreviewWebSocket.value.onclose = null;
            displayPreviewWebSocket.value.onerror = null;
            if (
              displayPreviewWebSocket.value.readyState === WebSocket.OPEN ||
              displayPreviewWebSocket.value.readyState === WebSocket.CONNECTING
            ) {
              displayPreviewWebSocket.value.close();
            }
          }

          let displayPreviewWsRetryCount = 0;
          const displayPreviewWsMaxRetries = 5;

          const connectDisplayPreviewWebSocket = () => {
            displayPreviewWebSocket.value = new WebSocket(
              process.env.VUE_APP_NESTJS_PREVIEW_DISPLAY_WS_URL ||
                "wss://v5.shopimind.com/template/spm-preview"
            );
            displayPreviewWebSocket.value.onopen = () => {
              displayPreviewWsRetryCount = 0;
              if (displayPreviewWebSocket.value) {
                // Join the preview room
                const joinMessage = {
                  event: "join-preview-room",
                  data: {
                    idShop,
                    idTemplate: selectedTemplateId.value
                  }
                };
                displayPreviewWebSocket.value.send(
                  JSON.stringify(joinMessage)
                );

                displayPreviewWebSocket.value.onmessage = messageData => {
                  const { data } = messageData;
                  if (data) {
                    let parsedData: any;
                    try {
                      parsedData = JSON.parse(data as string);
                    } catch (e) {
                      console.error("[Preview WS] Invalid JSON received:", e);
                      return;
                    }

                    if (
                      getTemplateParentTypeByType(
                        selectedTemplateType.value
                      ) === TemplateParentTypeEnum.EMBED &&
                      parsedData.event === "preview-selector-updated"
                    ) {
                      const { selector, position, lastUpdated } =
                        parsedData.data;
                      if (lastUpdated !== lastUpdateSelectorVersion.value) {
                        checkIfSavePointNeeded().then(() => {
                          const resolvedPosition = position || "append";
                          setTemplateConfigurationKeyInState(
                            "embedPosition",
                            resolvedPosition
                          );
                          setTemplateConfigurationKeyInState(
                            "embedSelector",
                            selector
                          );
                          lastUpdateSelectorVersion.value = lastUpdated;

                          // Save new selector in HTML
                          const template = getTemplateIframeDocument();
                          if (template) {
                            const spmBody =
                              template.querySelector("#spm_body");
                            if (spmBody) {
                              spmBody.setAttribute(
                                "data-target-element",
                                selector
                              );
                              spmBody.setAttribute(
                                "data-target-element-position",
                                resolvedPosition
                              );
                            }

                            // Update state
                            updateSectionsInState(false);

                            // Reuse shared funnel propagation logic on loaded templates
                            propagateEmbedSettingsToLoadedFunnel(
                              selector,
                              resolvedPosition,
                              false
                            );

                            // Create history point of save
                            createHistory(HistoryType.EMBED_CHANGE_POSITION);
                          }
                        });
                      }
                    }
                    if (parsedData.event === "preview-url-updated") {
                      checkIfSavePointNeeded().then(() => {
                        const { url } = parsedData.data;
                        const template = getTemplateIframeDocument();
                        if (template) {
                          const body = template.querySelector("#spm_body");
                          if (body) {
                            body.setAttribute("data-target-url", url);
                          }
                          store.commit(
                            "liveEditor/setDisplayUrl",
                            atob(url)
                          );

                          // Update state
                          updateSectionsInState(false);
                          createHistory(HistoryType.DISPLAY_CHANGE_URL);
                        }
                      });
                    }
                  }
                };
              }
            };

            displayPreviewWebSocket.value.onclose = () => {
              if (displayPreviewWsRetryCount < displayPreviewWsMaxRetries) {
                const delay = Math.min(
                  1000 * Math.pow(2, displayPreviewWsRetryCount),
                  30000
                );
                displayPreviewWsRetryCount++;
                setTimeout(connectDisplayPreviewWebSocket, delay);
              }
            };

            displayPreviewWebSocket.value.onerror = () => {
              if (
                displayPreviewWebSocket.value &&
                displayPreviewWebSocket.value.readyState !== WebSocket.CLOSED
              ) {
                displayPreviewWebSocket.value.close();
              }
            };
          };

          connectDisplayPreviewWebSocket();
        }
      } else {
        // Reset the flag after iframe reload
        isRestoringHistory.value = false;
      }
      updateFormWidgets();
      clearPlaceholder();
      await updateUsedSyncElements(idShop);
      refreshSmartProductsListsOriginalStructure();
      if (isDisplayTemplate(selectedTemplateType.value)) {
        const design = state.template ? state.template.sections[0] : null;
        if (state.leftToolbar.show.designPanel) {
          prepareStructureConfiguration();
          store.commit("liveEditor/refreshConfigurationPanelKey");
        }
        if (design) {
          const data = JSON.parse(design.data || "{}");
          if (
            data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] &&
            data[TEMPLATE_SYNC_ELEMENT_NAME]
          ) {
            const template = getTemplateIframeDocument();
            if (template) {
              const body = template.querySelector("#spm_body");
              if (body) {
                body.setAttribute(
                  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
                  data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER]
                );
                body.setAttribute(
                  TEMPLATE_SYNC_ELEMENT_NAME,
                  data[TEMPLATE_SYNC_ELEMENT_NAME]
                );
              }
            }
          }
        }
        updateFormsInState();
      }
      removeActiveItemElementDecorations();
      if (exitModeModalCallback.value) {
        await exitModeModalCallback.value();
      }

      // Log teaser data for backend after iframe is loaded and translated
      if (state.template?.type === TemplateTypeEnum.TEASER) {
        prepareTeaserDataForBackend();
      }
      } catch (e) {
        console.error('[TemplateEditor] iframeLoaded error:', e);
      } finally {
        done();
        store.commit("general/hideTheSpinner");
      }
    };

    const handleInsertElement = () => {
      destroyIframeSortables();
      livePreviewKey.value += 1;
    };

    const handleSyncElementUsedTemplatesLoaded = (total: number) => {
      syncElementUsedTemplatesTotal.value = total;
      syncElementUsedTemplatesLoaded.value = true;
    };

    const handleSyncElementNotUsedTemplatesLoaded = (total: number) => {
      syncElementNotUsedTemplatesTotal.value = total;
    };

    const handleNotUsedTemplatesSelectionChanged = (data: {
      selectedIds: number[];
      selectedSectionsByTemplate: Record<number, string[]>;
    }) => {
      selectedNotUsedTemplates.value = data.selectedIds;
      selectedNotUsedTemplatesSections.value = data.selectedSectionsByTemplate;
    };

    const resetNotUsedTemplatesSelection = () => {
      selectedNotUsedTemplates.value = [];
      selectedNotUsedTemplatesSections.value = {};
      // Reset selection in the component as well
      if (syncElementNotUsedTemplatesRef.value?.resetSelection) {
        syncElementNotUsedTemplatesRef.value.resetSelection();
      }
    };

    const handleSaveSyncElementTemplatesModalHide = () => {
      syncElementUsedTemplatesLoaded.value = false;
      resetNotUsedTemplatesSelection();
    };

    const handleUnlinkSyncElementModalHide = () => {
      syncElementUsedTemplatesLoaded.value = false;
    };

    const shouldShowNotUsedTemplates = computed(() => {
      if (!selectedSyncElement.value) return false;

      const {
        elementType,
        elementPartType,
        elementSubtype
      } = selectedSyncElement.value;

      // Show only if has elementPartType and elementType is section or widget or if element sync is design
      if (!elementPartType && elementType === TemplateStructureEnum.DESIGN)
        return true;

      if (elementType === TemplateStructureEnum.SECTION) return true;

      if (
        elementPartType === ElementPartTypeEnum.STYLES &&
        elementSubtype &&
        getWidgetType(elementSubtype) !== WidgetTypeEnum.IMAGE
      )
        return true;

      return false;
    });

    const getSyncElementUsedTemplatesNumberTitle = (unlink: boolean) => {
      if (!selectedSyncElement.value) return "";

      const {
        elementPartType,
        elementType,
        elementId
      } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t("templateBuilder.saveSyncElementModal.templateNumber", {
          element: t("templateBuilder.saveSyncElementModal.styleSync"),
          count: syncElementUsedTemplatesTotal.value
        });
      }

      if (elementType === TemplateStructureEnum.SECTION && !unlink) {
        const syncElementInFrame = getElementFromIframe(`#${elementId}`);
        if (syncElementInFrame) {
          const syncWidgets = Array.from(
            syncElementInFrame.querySelectorAll(
              `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}]`
            )
          );
          if (syncWidgets.length > 0) {
            return t(
              "templateBuilder.saveSyncElementModal.templateNumberWithChildSync",
              {
                element: t("templateBuilder.saveSyncElementModal.elementSync"),
                count: syncElementUsedTemplatesTotal.value,
                childElements: t(
                  "templateBuilder.saveSyncElementModal.childSyncElementsNumber",
                  { count: syncWidgets.length }
                )
              }
            );
          }
        }
      }

      return t("templateBuilder.saveSyncElementModal.templateNumber", {
        element: t("templateBuilder.saveSyncElementModal.elementSync"),
        count: syncElementUsedTemplatesTotal.value
      });
    };

    const getSyncElementUsedTemplatesNumberTitleWhenSaving = (
      unlink: boolean
    ) => {
      const label = unlink
        ? "templateBuilder.saveSyncElementModal.erase.unlinkLabel"
        : "templateBuilder.saveSyncElementModal.erase.syncLabel";
      return t(label, {
        count: syncElementUsedTemplatesTotal.value
      });
    };

    const syncElementUsedTemplateListLabel = computed(() => {
      if (!selectedSyncElement.value) return "";

      const { elementPartType } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t("templateBuilder.saveSyncElementModal.templateListLabel", {
          element: t(
            "templateBuilder.saveSyncElementModal.styleSync"
          ).toLowerCase()
        });
      }

      return t("templateBuilder.saveSyncElementModal.templateListLabel", {
        element: t(
          "templateBuilder.saveSyncElementModal.elementSync"
        ).toLowerCase()
      });
    });

    const syncElementUsedTemplateListLabelForUnlink = computed(() => {
      if (!selectedSyncElement.value) return "";

      const { elementPartType } = selectedSyncElement.value;

      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          "templateBuilder.saveSyncElementModal.templateListLabelForUnlink",
          {
            element: t(
              "templateBuilder.saveSyncElementModal.styleSync"
            ).toLowerCase()
          }
        );
      }

      return t(
        "templateBuilder.saveSyncElementModal.templateListLabelForUnlink",
        {
          element: t(
            "templateBuilder.saveSyncElementModal.elementSync"
          ).toLowerCase()
        }
      );
    });

    const syncElementNotUsedTemplatesDescription = computed(() => {
      if (!selectedSyncElement.value) return "";

      const { elementPartType } = selectedSyncElement.value;
      if (elementPartType === ElementPartTypeEnum.STYLES) {
        return t(
          "templateBuilder.saveSyncElementModal.notUsedTemplatesDescription",
          {
            element: t(
              "templateBuilder.saveSyncElementModal.styleSync"
            ).toLowerCase()
          }
        );
      }
      return t(
        "templateBuilder.saveSyncElementModal.notUsedTemplatesDescription",
        {
          element: t(
            "templateBuilder.saveSyncElementModal.designSync"
          ).toLowerCase()
        }
      );
    });

    const hideExitEditModeModal = () => {
      store.commit("liveEditor/setExitEditModeModalVisible", false);
      store.commit("liveEditor/setExitEditModeCallback", null);
    };

    const isExitCurrentChangesModalVisible = computed(
      () => store.getters["liveEditor/getIsExitCurrentChangesModalVisible"]
    );

    const hideExitCurrentChangeModal = () => {
      store.commit("liveEditor/setIsExitCurrentChangesModalVisible", false);
    };

    const confirmationModal = computed(
      () => store.getters["liveEditor/getConfirmationModal"]
    );

    watch(
      confirmationModal,
      () => {
        if (confirmationModal.value) {
          confirm.require({
            group: "confirmationModal",
            message: confirmationModal.value.message,
            header: confirmationModal.value.title,
            icon: "far fa-exclamation-triangle",
            acceptLabel: t("yes"),
            acceptClass: "p-button-danger",
            rejectLabel: t("no"),
            rejectClass: "p-button-secondary",
            accept: async () => {
              await confirmationModal.value.acceptCallback();
            },
            reject: async () => {
              await confirmationModal.value.rejectCallback();
            }
          });
        }
      },
      { deep: true }
    );

    return {
      width,
      selectedTemplateId,
      selectedTemplateName,
      selectedTemplateType,
      selectedStructure,
      selectedStructureActions,
      state,
      currentConflictMessage,
      currentConflictLocalDate,
      currentConflictRemoteDate,
      isLeftToolbarVisible,
      toolbarRightButtons,
      contentHeaderButtonsLeft,
      tabs,
      livePreviewKey,
      historyList,
      selectedHistory,
      displayRenameTemplateModal,
      valueRenameTemplateModal,
      displaySaveElementModal,
      typeSaveElementModal,
      nameSaveElementModal,
      syncStateSaveElementModal,
      saveElementModalRef,
      displaySearchElementsModal,
      searchElementsType,
      searchElementsPartType,
      searchElementsSubtype,
      includeAllElementsPartType,
      displaySyncElementTemplatesModal,
      rename,
      handleChangeHistory,
      handleLoadMoreHistory,
      historyHasMore,
      historyIsLoadingMore,
      historyTotalItems,
      saveElement,
      handleSaveElementClose,
      handleSearchElements,
      handleSearchElementsClose,
      handleSearchElementSelected,
      sendTestTemplate,
      showTemplateSelector,
      closeTemplateSelector,
      selectTemplateFromTemplateSelector,
      iframeLoaded,
      isConfigurationPanelClosable,
      informationPanelValidation,
      newListName,
      t,
      showDuplicationModal,
      closeDuplicationModal,
      duplicateConfirmationDialog,
      showSaveToMyTemplatesModal,
      saveToMyTemplatesName,
      saveToMyTemplatesConfirm,
      closeSaveToMyTemplatesModal,
      isChooseZipFileModalVisible,
      isWidgetPanelClosable,
      isTranslationPanelClosable,
      widgetsPanelKey,
      generateSha1HashWithSalt,
      MEDIA_URL_PART_THUMB_GENERATION,
      toEraseElementId,
      subtitle,
      idShop,
      displayElementsModal,
      handleInsertElement,
      displaySaveSyncElementTemplatesModal,
      saveForAllInstancesLoading,
      selectedSyncElement,
      handleSyncElementUsedTemplatesLoaded,
      syncElementUsedTemplatesLoaded,
      handleSyncElementNotUsedTemplatesLoaded,
      handleNotUsedTemplatesSelectionChanged,
      handleSaveSyncElementTemplatesModalHide,
      syncElementNotUsedTemplatesRef,
      shouldShowNotUsedTemplates,
      exitModeModalVisible,
      hideExitEditModeModal,
      commandSaveForAllInstances,
      commandUnlinkAndSave,
      commandCancelModifications,
      isExitCurrentChangesModalVisible,
      hideExitCurrentChangeModal,
      saveAndLeaveCommand,
      leaveWithoutSavingCommand,
      funnels,
      funnelStepRoot,
      funnelStepKey,
      canShowTeaserButton,
      canShowAddTeaserButton,
      canShowGoToTeaserButton,
      isOnTeaser,
      handleAddTeaser,
      handleGoToTeaser,
      handleGoToPopup,
      teaserMenuRef,
      toggleTeaserMenu,
      teaserMenuItems,
      contentHeaderEndToolbar,
      whatsappCombinedStatus,
      whatsappStatusInfoMessage,
      getWhatsappStatusIcon,
      getWhatsappStatusColors,
      getWhatsappStatusLabel,
      isSendTestModalVisible,
      isSendTestEmailV4ModalVisible,
      isPreviewDisplayV4ModalVisible,
      isMigrateV4ModalVisible,
      sendTestTemplateV4,
      getSyncElementUsedTemplatesNumberTitle,
      syncElementUsedTemplateListLabel,
      syncElementNotUsedTemplatesDescription,
      displayUnlinkSyncElementModal,
      handleUnlinkSyncElementModalHide,
      unlinkSyncElementLoading,
      showUnlinkAndSaveModal,
      syncElementUsedTemplateListLabelForUnlink,
      syncElementUsedTemplatesRef,
      syncElementUsedTemplatesForSaveRef,
      displayActiveCampaignsWarningModal,
      activeCampaignsForWarning,
      confirmSaveWithActiveCampaigns,
      handleSaveForAllInstances,
      toEraseElementSyncId,
      getSyncElementUsedTemplatesNumberTitleWhenSaving,
      toEraseIsNewSync,
      confirmWhatsappHashChange,
      cancelWhatsappHashChange,
      showPreviewUrlModal,
      previewUrlPath,
      previewUrlError,
      previewUrlLoading,
      shopBaseUrl,
      confirmPreviewUrl
    };
  }
});
